import PasswordInput from '../components/PasswordInput';
import { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import TermGradeReportCard from '../components/TermGradeReportCard';
import {
  CheckCircleIcon, XCircleIcon, ClockIcon,
  PlusIcon, XMarkIcon, PrinterIcon,
  TableCellsIcon, ListBulletIcon,
} from '@heroicons/react/24/outline';

// Cloudinary "raw" files (docx/pptx/etc) don't carry a Content-Disposition
// filename on their own — older uploads don't even have an extension in the
// stored URL (fixed for new uploads, but existing ones are stuck with it since
// a Cloudinary public_id can't be renamed after the fact). Cloudinary's
// fl_attachment transform lets us force both a real filename AND the correct
// extension at request time, regardless of what the underlying URL looks like.
const MATERIAL_TYPE_EXT = { pdf: 'pdf', doc: 'docx', ppt: 'pptx' };
const materialDownloadUrl = (m) => {
  if (!m?.fileUrl || !m.fileUrl.includes('/upload/')) return m?.fileUrl;
  if (['image', 'video'].includes(m.type)) return m.fileUrl; // preview inline, no forced download
  const urlExt = (m.fileUrl.split('/').pop().split('.')[1] || '').split(/[?#]/)[0];
  const ext = (urlExt && urlExt.length <= 5 ? urlExt : MATERIAL_TYPE_EXT[m.type]) || '';
  const safeTitle = (m.title || 'material').replace(/[^a-zA-Z0-9_ -]/g, '').trim().slice(0, 60) || 'material';
  const filename = ext ? `${safeTitle}.${ext}` : safeTitle;
  return m.fileUrl.replace('/upload/', `/upload/fl_attachment:${encodeURIComponent(filename)}/`);
};

// ─── Shared style ─────────────────────────────────────────────────────────────
const sharedStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sp * { box-sizing:border-box; }
  .sp { font-family:'Sora',sans-serif; color:#1a1d2e; animation:spFade .4s ease both; }
  @keyframes spFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .sp-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .sp-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .sp-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-purple{ background:#8b5cf6; color:white; }
  .btn-purple:hover { background:#7c3aed; box-shadow:0 4px 14px rgba(139,92,246,.35); transform:translateY(-1px); }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover { background:#ef4444; color:white; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }
  .stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:.9rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.1rem; border:1px solid #e8ebf4; }
  .s-icon { width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; margin-bottom:.6rem; }
  .s-icon svg { width:15px; height:15px; stroke-width:1.8; }
  .s-icon.green{background:#ecfdf5;color:#10c98e} .s-icon.red{background:#fef2f2;color:#ef4444}
  .s-icon.amber{background:#fffbeb;color:#f59e0b} .s-icon.blue{background:#eef2ff;color:#3b6ef8}
  .s-icon.purple{background:#f3f0ff;color:#8b5cf6}
  .s-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .s-value { font-size:1.4rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; }
  .s-sub   { font-size:.68rem; color:#b0b5cc; margin-top:3px; }
  .filter-row { display:flex; gap:10px; margin-bottom:1.2rem; flex-wrap:wrap; }
  .filter-select,.filter-input { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; color:#1a1d2e; outline:none; }
  .filter-select:focus,.filter-input:focus { border-color:#8b5cf6; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .sp-table { width:100%; border-collapse:collapse; }
  .sp-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .sp-table th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .sp-table tbody tr { border-bottom:1px solid #f3f5fb; }
  .sp-table tbody tr:last-child { border-bottom:none; }
  .sp-table tbody tr:hover { background:#fafbff; }
  .sp-table td { padding:10px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .status-pill { display:inline-flex; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; }
  .status-pill.present  { background:#ecfdf5; color:#10c98e; }
  .status-pill.absent   { background:#fef2f2; color:#ef4444; }
  .status-pill.late     { background:#fffbeb; color:#f59e0b; }
  .status-pill.on-leave { background:#eef2ff; color:#3b6ef8; }
  .status-pill.paid     { background:#ecfdf5; color:#10c98e; }
  .status-pill.unpaid   { background:#fef2f2; color:#ef4444; }
  .status-pill.overdue  { background:#fff7ed; color:#f97316; }
  .status-pill.pending  { background:#fffbeb; color:#f59e0b; }
  .status-pill.approved { background:#ecfdf5; color:#10c98e; }
  .status-pill.rejected { background:#fef2f2; color:#ef4444; }
  .mono { font-family:'JetBrains Mono',monospace; font-size:.76rem; color:#6b7194; }
  .sp-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .sp-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#8b5cf6; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:flex-start; justify-content:center; z-index:1000; padding:24px 16px; overflow-y:auto; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; max-width:460px; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; margin:auto; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:18px; flex-wrap:wrap; }
  .form-field { display:flex; flex-direction:column; gap:5px; margin-bottom:14px; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-textarea:focus { border-color:#8b5cf6; background:white; }
  .form-textarea { resize:vertical; min-height:70px; }
  .mat-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem 1.1rem; transition:all .15s; }
  .mat-card:hover { border-color:#8b5cf6; box-shadow:0 4px 14px rgba(139,92,246,.1); transform:translateY(-1px); }
  .mat-chip { display:inline-flex; font-size:.65rem; font-weight:700; padding:2px 8px; border-radius:6px; text-transform:uppercase; margin-bottom:8px; }
  .mat-chip.pdf   { background:#fee2e2; color:#ef4444; }
  .mat-chip.video { background:#ede9fe; color:#8b5cf6; }
  .mat-chip.youtube { background:#fee2e2; color:#ef4444; }
  .mat-chip.image { background:#d1fae5; color:#10c98e; }
  .mat-chip.doc   { background:#dbeafe; color:#3b6ef8; }
  .mat-chip.ppt   { background:#fef3c7; color:#f59e0b; }
  .mat-chip.other { background:#f3f4f6; color:#6b7280; }
  .gr-subject { background:white; border-radius:14px; border:1px solid #e8ebf4; margin-bottom:1rem; overflow:hidden; }
  .gr-subject-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; background:#fafbff; border-bottom:1px solid #edf0f9; flex-wrap:wrap; gap:6px; }
  .gr-subject-name { font-size:.86rem; font-weight:700; color:#0f1123; }
  .gr-pct-badges { display:flex; gap:8px; flex-wrap:wrap; }
  .gr-pct-badge  { font-size:.68rem; font-weight:600; padding:3px 9px; border-radius:99px; }
  .gr-pct-badge.mine    { background:#f3f0ff; color:#8b5cf6; }
  .gr-pct-badge.highest { background:#ecfdf5; color:#10c98e; }
  .gr-pct-badge.average { background:#eef2ff; color:#3b6ef8; }
  .gr-marks-row { display:flex; gap:0; overflow-x:auto; -webkit-overflow-scrolling:touch; }
  .gr-mark-cell { flex:0 0 auto; min-width:90px; padding:10px 14px; border-right:1px solid #f5f6fb; text-align:center; }
  .gr-mark-cell:last-child { border-right:none; }
  .gr-mark-type { font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-bottom:4px; }
  .gr-mark-max  { font-size:.7rem; color:#b0b5cc; }
  .gr-mark-obtained { font-size:1rem; font-weight:700; font-family:'JetBrains Mono',monospace; color:#0f1123; }
  .gr-mark-absent { font-size:.75rem; color:#ef4444; font-weight:600; }
  @media print { .no-print{display:none!important} body *{visibility:hidden} .print-area,.print-area *{visibility:visible} .print-area{position:absolute;left:0;top:0;width:100%} }

  .sp-cards { display:none; padding:10px; }
  .sp-card { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .sp-card-top { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .sp-card-row { display:flex; justify-content:space-between; align-items:center; font-size:.78rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .sp-card-row:last-of-type { border-bottom:none; }
  .sp-card-label { color:#9094a8; font-weight:600; flex-shrink:0; }

  /* ── Mobile shared ── */
  @media (max-width:768px) {
    .sp-title { font-size:1.1rem; }
    .stat-grid { grid-template-columns:repeat(2,1fr); }
    .filter-row { flex-direction:column; gap:8px; }
    .filter-input,.filter-select { width:100%; }
    .modal { padding:18px; }
    .modal-btns { justify-content:stretch; }
    .modal-btns .btn { flex:1; justify-content:center; }
    .sp-tbl-wrap { display:none !important; }
    .sp-cards { display:block; }
  }

  /* ── View tabs ── */
  .ta-toolbar { display:flex; gap:.7rem; flex-wrap:wrap; align-items:center; margin-bottom:1.2rem; }
  .view-tabs  { display:flex; gap:5px; }
  .view-btn   { padding:7px 18px; border-radius:9px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .view-btn:hover  { border-color:#8b5cf6; color:#8b5cf6; }
  .view-btn.active { background:#8b5cf6; color:white; border-color:#8b5cf6; }
  /* ── Monthly bar ── */
  .month-bar { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem 1.4rem; margin-bottom:1rem; display:flex; align-items:center; flex-wrap:wrap; gap:.5rem; }
  .mb-title  { font-size:.9rem; font-weight:700; color:#0f1123; white-space:nowrap; margin-right:.8rem; }
  .mb-items  { display:flex; flex:1; min-width:0; flex-wrap:wrap; }
  .mb-item   { flex:1; min-width:68px; text-align:center; padding:0 .6rem; border-left:1px solid #f0f2f7; }
  .mb-item:first-child { border-left:none; }
  .mb-lbl    { font-size:.6rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; margin-bottom:5px; }
  .mb-val    { width:30px; height:30px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; font-size:.88rem; font-weight:700; }
  /* ── Yearly grid — same as teacher attendance ── */
  .yr-grid      { display:grid; grid-template-columns:repeat(auto-fill,minmax(260px,1fr)); gap:1rem; }
  .yr-card      { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.3rem; cursor:pointer; transition:box-shadow .15s; }
  .yr-card:hover { box-shadow:0 4px 18px rgba(0,0,0,.07); border-color:#c4b5fd; }
  .yr-card-title { font-size:.9rem; font-weight:700; color:#0f1123; margin-bottom:.9rem; }
  .yr-months-row { display:grid; grid-template-columns:repeat(6,1fr); gap:5px; margin-bottom:5px; }
  .yr-m     { text-align:center; }
  .yr-m-lbl { font-size:.58rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.04em; margin-bottom:3px; }
  .yr-m-bar { height:34px; border-radius:7px; display:flex; align-items:center; justify-content:center; font-size:.66rem; font-weight:700; }
  .yr-totals { display:flex; gap:10px; flex-wrap:wrap; padding-top:.75rem; margin-top:.4rem; border-top:1px solid #f0f2f7; align-items:center; }
  .yr-tot    { font-size:.7rem; font-weight:600; }
  .status-pill.holiday { background:#f3f0ff; color:#8b5cf6; }
  .status-pill.off     { background:#f0f2f7; color:#6b7194; }
`;

const fmtDate     = (d) => d ? new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'}) : '—';
const fmtDateShort= (d) => d ? new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short'}) : '—';
const dayOfWeek   = (d) => new Date(d).toLocaleDateString('en-US',{weekday:'short'});
const curMonth    = () => new Date().toISOString().slice(0,7);
const curYear     = () => { const y=new Date().getFullYear(); return `${y}-${y+1}`; };

// ─── ATTENDANCE ────────────────────────────────────────────────────────────────
export const StudentAttendance = () => {
  const [view,      setView]      = useState('daily');
  const [month,     setMonth]     = useState(curMonth());
  const [year,      setYear]      = useState(new Date().getFullYear());
  const [monthData, setMonthData] = useState(null);
  const [todayRec,  setTodayRec]  = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [yLoading,  setYLoading]  = useState(false);
  const [yearData,  setYearData]  = useState([]);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaves,    setLeaves]    = useState([]);
  const [leaveForm, setLeaveForm] = useState({ startDate:'', endDate:'', reason:'' });
  const [saving,    setSaving]    = useState(false);

  const fetchMonthly = useCallback(async (signal) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/student/attendance?month=${month}`, { signal });

      // Fill ALL dates from month start to today with records or 'no-record'
      const [y, m] = month.split('-').map(Number);
      const today  = new Date(); today.setHours(0,0,0,0);
      const start  = new Date(y, m-1, 1);
      const end    = new Date(Math.min(today, new Date(y, m, 0))); // end of month or today

      const existingMap = {};
      (data?.records||[]).forEach(r => {
        const key = new Date(r.date).toISOString().slice(0,10);
        existingMap[key] = r;
      });

      const allRecords = [];
      const cur = new Date(start);
      while (cur <= end) {
        const key = cur.toISOString().slice(0,10);
        allRecords.push(existingMap[key] || { date: new Date(cur), status: 'no-record', lateMinutes: 0 });
        cur.setDate(cur.getDate() + 1);
      }

      setMonthData({ ...data, records: allRecords });

      const todayKey = today.toISOString().slice(0,10);
      setTodayRec(existingMap[todayKey] || null);
    } catch(e) { if (e.code==='ERR_CANCELED') return; toast.error('Failed'); }
    finally    { setLoading(false); }
  }, [month]);

  const fetchYearly = async () => {
    setYLoading(true);
    try {
      const months = await Promise.all(
        Array.from({ length:12 }, (_, i) => {
          const m = String(i+1).padStart(2,'0');
          return api.get(`/student/attendance?month=${year}-${m}`)
            .then(r  => ({ month: i+1, summary: r.data?.summary||{}, records: r.data?.records||[] }))
            .catch(() => ({ month: i+1, summary: {}, records: [] }));
        })
      );
      setYearData(months);
    } catch { toast.error('Failed to load yearly data'); }
    finally   { setYLoading(false); }
  };

  useEffect(() => {
    const c = new AbortController();
    fetchMonthly(c.signal);
    return () => c.abort();
  }, [fetchMonthly]);

  useEffect(() => { if (view==='yearly') fetchYearly(); }, [year, view]);

  useEffect(() => {
    api.get('/student/leaves').then(r => setLeaves(r.data||[])).catch(()=>{});
  }, []);

  const handleApplyLeave = async () => {
    if (!leaveForm.startDate||!leaveForm.endDate) { toast.error('Select dates'); return; }
    if (!leaveForm.reason.trim()) { toast.error('Reason is required'); return; }
    try {
      setSaving(true);
      const { data:l } = await api.post('/student/leaves', leaveForm);
      setLeaves(p => [l,...p]);
      toast.success('Leave applied');
      setLeaveOpen(false);
      setLeaveForm({ startDate:'', endDate:'', reason:'' });
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
    finally    { setSaving(false); }
  };

  const handleCancelLeave = async (id) => {
    try { await api.delete(`/student/leaves/${id}`); setLeaves(p=>p.filter(l=>l._id!==id)); toast.success('Cancelled'); }
    catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const { summary, records, attendancePct } = monthData || {};

  const ms = summary || (records ? {
    present:  records.filter(r=>r.status==='present').length,
    absent:   records.filter(r=>r.status==='absent').length,
    onLeave:  records.filter(r=>r.status==='on-leave').length,
    holiday:  records.filter(r=>r.status==='holiday').length,
    lateCount:records.filter(r=>r.status!=='no-record'&&(r.lateMinutes||0)>0).length,
    totalLateMinutes: records.filter(r=>r.status!=='no-record').reduce((s,r)=>s+(r.lateMinutes||0),0),
  } : null);

  const monthLabel = new Date(month+'-01').toLocaleDateString('en-PK',{month:'long',year:'numeric'});
  const MONTHS_ARR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}) : '—';

  const StatusPill = ({ r }) => {
    if (r.status==='holiday')   return <span className="status-pill holiday">🎉 {r.note||'Holiday'}</span>;
    if (r.status==='off')       return <span className="status-pill off">📅 Off Day</span>;
    if (r.status==='no-record') return <span style={{fontSize:'.67rem',color:'#c0c5dc',fontStyle:'italic'}}>—</span>;
    return <span className={`status-pill ${r.status}`}>{r.status}{r.leaveType&&r.status==='on-leave'?` (${r.leaveType})`:''}</span>;
  };

  const AttTable = ({ recs }) => (
    <>
      <div className="sp-tbl-wrap" style={{overflowX:'auto'}}>
        <table className="sp-table">
          <thead><tr><th>Date</th><th>Day</th><th>Status</th><th>Late Min</th></tr></thead>
          <tbody>
            {recs.map((r,i)=>(
              <tr key={i}>
                <td><span className="mono">{fmtDateShort(r.date)}</span></td>
                <td><span style={{fontSize:'.74rem',color:'#9094a8'}}>{dayOfWeek(r.date)}</span></td>
                <td><StatusPill r={r} /></td>
                <td><span className="mono">{r.lateMinutes>0?`${r.lateMinutes}m`:'—'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="sp-cards">
        {recs.map((r,i)=>(
          <div key={i} className="sp-card">
            <div className="sp-card-top">
              <div>
                <div style={{fontWeight:700,fontSize:'.84rem',color:'#0f1123'}}>{fmtDateShort(r.date)}</div>
                <div style={{fontSize:'.7rem',color:'#9094a8'}}>{dayOfWeek(r.date)}</div>
              </div>
              <StatusPill r={r} />
            </div>
            <div className="sp-card-row"><span className="sp-card-label">Late Min</span><span className="mono">{r.lateMinutes>0?`${r.lateMinutes}m`:'—'}</span></div>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="sp"><style>{sharedStyle}</style>
      <div className="sp-header">
        <div>
          <div className="sp-title">My Attendance</div>
          <div className="sp-sub">View your daily attendance and apply for leave</div>
        </div>
        <button className="btn btn-purple" onClick={()=>setLeaveOpen(true)}><PlusIcon />Apply Leave</button>
      </div>

      {/* Toolbar */}
      <div className="ta-toolbar">
        {view==='monthly' && <input type="month"  className="filter-input" value={month} onChange={e=>setMonth(e.target.value)} />}
        {view==='yearly'  && <input type="number" className="filter-input" value={year} min="2020" max="2099" onChange={e=>setYear(Number(e.target.value))} style={{width:100}} />}
        <div className="view-tabs" style={{marginLeft:'auto'}}>
          {['daily','monthly','yearly'].map(v=>(
            <button key={v} className={`view-btn ${view===v?'active':''}`} onClick={()=>setView(v)}>
              {v.charAt(0).toUpperCase()+v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* ══ DAILY ══ */}
      {view==='daily' && (
        loading
          ? <div className="sp-loading"><div className="spinner"/>Loading…</div>
          : <>
              {/* Today status card */}
              <div style={{background:todayRec?.status==='present'?'linear-gradient(135deg,#10c98e,#3b6ef8)':todayRec?.status==='absent'?'linear-gradient(135deg,#ef4444,#f97316)':todayRec?.status==='holiday'?'linear-gradient(135deg,#8b5cf6,#6366f1)':'linear-gradient(135deg,#9094a8,#6b7194)',borderRadius:16,padding:'1.4rem 1.6rem',color:'white',marginBottom:'1.2rem',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'1rem',flexWrap:'wrap'}}>
                <div>
                  <div style={{fontSize:'.78rem',fontWeight:600,opacity:.85,marginBottom:4}}>Today's Status</div>
                  <div style={{fontSize:'1.1rem',fontWeight:700}}>
                    {todayRec?.status==='present'?'✓ Present':todayRec?.status==='absent'?'✗ Absent':todayRec?.status==='holiday'?'🎉 Holiday':todayRec?.status==='on-leave'?'◑ On Leave':todayRec?.status==='off'?'📅 Off Day':'No record yet'}
                  </div>
                  {(todayRec?.lateMinutes||0)>0&&<div style={{fontSize:'.75rem',marginTop:4,opacity:.9}}>⚠️ {todayRec.lateMinutes} minutes late</div>}
                </div>
                {ms&&<div style={{display:'flex',gap:'1.5rem',flexWrap:'wrap'}}>
                  {[{l:'Present',v:ms.present||0},{l:'Absent',v:ms.absent||0},{l:'Leave',v:ms.onLeave||0}].map(({l,v})=>(
                    <div key={l} style={{textAlign:'center'}}>
                      <div style={{fontSize:'.65rem',opacity:.7,fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em'}}>{l}</div>
                      <div style={{fontSize:'1rem',fontWeight:700,marginTop:2}}>{v}</div>
                    </div>
                  ))}
                </div>}
              </div>
              {/* Today's record row */}
              <div className="panel">
                {todayRec
                  ? <AttTable recs={[todayRec]} />
                  : <div className="sp-empty">No attendance record for today yet.</div>
                }
              </div>
            </>
      )}

      {/* ══ MONTHLY ══ */}
      {view==='monthly' && (
        loading
          ? <div className="sp-loading"><div className="spinner"/>Loading…</div>
          : <>
              {ms&&(
                <div className="month-bar">
                  <div className="mb-title">{monthLabel}</div>
                  <div className="mb-items">
                    {[
                      {lbl:'Present', val:ms.present||0,  color:'#10c98e', bg:'#ecfdf5'},
                      {lbl:'Absent',  val:ms.absent||0,   color:'#ef4444', bg:'#fef2f2'},
                      {lbl:'Leave',   val:ms.onLeave||0,  color:'#3b6ef8', bg:'#eef2ff'},
                      {lbl:'Holiday', val:ms.holiday||0,  color:'#8b5cf6', bg:'#f5f3ff'},
                      {lbl:'Late',    val:ms.lateCount||0,color:'#f59e0b', bg:'#fffbeb'},
                    ].map(({lbl,val,color,bg})=>(
                      <div key={lbl} className="mb-item">
                        <div className="mb-lbl">{lbl}</div>
                        <div className="mb-val" style={{background:bg,color}}>{val}</div>
                      </div>
                    ))}
                    {attendancePct!=null&&<div className="mb-item">
                      <div className="mb-lbl">Pct</div>
                      <div className="mb-val" style={{background:attendancePct>=90?'#ecfdf5':attendancePct>=70?'#fffbeb':'#fef2f2',color:attendancePct>=90?'#10c98e':attendancePct>=70?'#f59e0b':'#ef4444'}}>{attendancePct}%</div>
                    </div>}
                  </div>
                </div>
              )}
              <div className="panel">
                <div className="panel-head"><div className="panel-title">Daily Records — {monthLabel}</div></div>
                {!records?.length
                  ? <div className="sp-empty">No attendance records for this month.</div>
                  : <AttTable recs={records} />
                }
              </div>
            </>
      )}

      {/* ══ YEARLY ══ */}
      {view==='yearly' && (
        yLoading
          ? <div className="sp-loading"><div className="spinner"/>Loading yearly data…</div>
          : <div className="yr-grid">
              {yearData.map(({month:m, summary:s, records:recs})=>{
                const present = s.present  || recs.filter(r=>r.status==='present').length;
                const absent  = s.absent   || recs.filter(r=>r.status==='absent').length;
                const onLeave = s.onLeave  || recs.filter(r=>r.status==='on-leave').length;
                const holiday = s.holiday  || recs.filter(r=>r.status==='holiday').length;
                const late    = s.totalLateMinutes ?? recs.reduce((a,r)=>a+(r.lateMinutes||0),0);
                const total   = present+absent+onLeave;
                const pct     = total>0?Math.round((present/total)*100):null;
                const bg  = pct===null?'#f0f2f7':pct>=90?'#ecfdf5':pct>=70?'#fffbeb':pct>0?'#fef2f2':'#f0f2f7';
                const col = pct===null?'#c0c5dc':pct>=90?'#10c98e':pct>=70?'#f59e0b':pct>0?'#ef4444':'#c0c5dc';
                const barFor = (idx) => (
                  <div key={idx} className="yr-m">
                    <div className="yr-m-lbl">{MONTHS_ARR[idx-1]}</div>
                    <div className="yr-m-bar" style={{background:idx===m?bg:'#f0f2f7',color:idx===m?col:'#c0c5dc'}}>
                      {idx===m&&pct!==null?`${pct}%`:'—'}
                    </div>
                  </div>
                );
                return (
                  <div key={m} className="yr-card" onClick={()=>{setMonth(`${year}-${String(m).padStart(2,'0')}`);setView('monthly');}}>
                    <div className="yr-card-title">{MONTHS_ARR[m-1]} {year}</div>
                    <div className="yr-months-row">{[1,2,3,4,5,6].map(barFor)}</div>
                    <div className="yr-months-row">{[7,8,9,10,11,12].map(barFor)}</div>
                    <div className="yr-totals">
                      <span className="yr-tot" style={{color:'#10c98e'}}>✓ {present} present</span>
                      <span className="yr-tot" style={{color:'#ef4444'}}>✗ {absent} absent</span>
                      <span className="yr-tot" style={{color:'#f59e0b'}}>◑ {onLeave} leave</span>
                      {holiday>0&&<span className="yr-tot" style={{color:'#8b5cf6'}}>🎉 {holiday}</span>}
                      {late>0   &&<span className="yr-tot" style={{color:'#9094a8'}}>⏱ {late}m late</span>}
                    </div>
                  </div>
                );
              })}
            </div>
      )}

      {/* Leave applications */}
      {leaves.length>0&&(
        <div className="panel" style={{marginTop:'1.2rem'}}>
          <div className="panel-head"><div className="panel-title">My Leave Applications ({leaves.length})</div></div>
          <div className="sp-tbl-wrap" style={{overflowX:'auto'}}>
            <table className="sp-table">
              <thead><tr><th>Dates</th><th>Reason</th><th>Status</th><th>Note</th><th></th></tr></thead>
              <tbody>
                {leaves.map(l=>(
                  <tr key={l._id}>
                    <td><span className="mono">{fmtDateShort(l.startDate)}{l.startDate!==l.endDate?` → ${fmtDateShort(l.endDate)}`:''}</span></td>
                    <td style={{fontSize:'.76rem',color:'#6b7194',maxWidth:160}}>{l.reason||'—'}</td>
                    <td><span className={`status-pill ${l.status}`}>{l.status}</span></td>
                    <td style={{fontSize:'.72rem',color:'#6b7194',fontStyle:'italic'}}>{l.adminNote||'—'}</td>
                    <td>{l.status==='pending'&&<button className="btn btn-red btn-sm" onClick={()=>handleCancelLeave(l._id)}>Cancel</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="sp-cards">
            {leaves.map(l=>(
              <div key={l._id} className="sp-card">
                <div className="sp-card-top">
                  <span className="mono">{fmtDateShort(l.startDate)}{l.startDate!==l.endDate?` → ${fmtDateShort(l.endDate)}`:''}</span>
                  <span className={`status-pill ${l.status}`}>{l.status}</span>
                </div>
                <div className="sp-card-row"><span className="sp-card-label">Reason</span><span style={{textAlign:'right'}}>{l.reason||'—'}</span></div>
                {l.adminNote && <div className="sp-card-row"><span className="sp-card-label">Note</span><span style={{textAlign:'right',fontStyle:'italic'}}>{l.adminNote}</span></div>}
                {l.status==='pending' && <div style={{marginTop:8}}><button className="btn btn-red btn-sm" onClick={()=>handleCancelLeave(l._id)}>Cancel</button></div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {leaveOpen&&(
        <div className="modal-backdrop" onClick={()=>setLeaveOpen(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Apply for Leave</div><button className="modal-close" onClick={()=>setLeaveOpen(false)}><XMarkIcon /></button></div>
            <div className="form-field"><label className="form-label">Start Date</label><input type="date" className="form-input" value={leaveForm.startDate} onChange={e=>setLeaveForm(p=>({...p,startDate:e.target.value}))} /></div>
            <div className="form-field"><label className="form-label">End Date</label><input type="date" className="form-input" value={leaveForm.endDate} onChange={e=>setLeaveForm(p=>({...p,endDate:e.target.value}))} /></div>
            <div className="form-field"><label className="form-label">Reason</label><textarea className="form-input" rows={3} required value={leaveForm.reason} onChange={e=>setLeaveForm(p=>({...p,reason:e.target.value}))} /></div>
            <div className="modal-btns"><button className="btn btn-ghost" onClick={()=>setLeaveOpen(false)}>Cancel</button><button className="btn btn-purple" onClick={handleApplyLeave} disabled={saving}>{saving?'Saving…':'Apply Leave'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
};


// ─── RESULTS ──────────────────────────────────────────────────────────────────
export const StudentResults = () => {
  const [year,         setYear]        = useState(curYear());
  const [data,         setData]        = useState(null);
  const [loading,      setLoading]     = useState(true);
  const [activeSubject,setActiveSubject]= useState(null); // null = card grid view
 
  const fetchData = useCallback(async (signal) => {
    try {
      setLoading(true);
      const { data: r } = await api.get(`/student/results?academicYear=${year}`, { signal });
      setData(r);
      setActiveSubject(null); // reset to card grid on year change
    } catch(e) { if (e.code === 'ERR_CANCELED') return; toast.error('Failed to load results'); }
    finally { setLoading(false); }
  }, [year]);
 
  useEffect(() => {
    const c = new AbortController();
    fetchData(c.signal);
    return () => c.abort();
  }, [fetchData]);
 
  const pctColor = (p) => p >= 80 ? '#10c98e' : p >= 60 ? '#f59e0b' : '#ef4444';
  const pctBg    = (p) => p >= 80 ? '#ecfdf5' : p >= 60 ? '#fffbeb' : '#fef2f2';
 
  const fmtAssessmentDate = (d) => d
    ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }).replace(/ /g, ' ')
    : '—';
 
  const SUBJ_COLORS = ['#3b6ef8','#10c98e','#8b5cf6','#f59e0b','#ef4444','#06b6d4','#ec4899','#14b8a6'];
 
  const resultsStyle = `
    .sr-cards { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:1rem; }
    .sr-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1.1rem 1.2rem; cursor:pointer; transition:all .15s; }
    .sr-card:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(0,0,0,.08); }
    .sr-card-icon { width:40px; height:40px; border-radius:11px; display:flex; align-items:center; justify-content:center; font-size:.78rem; font-weight:700; margin-bottom:10px; }
    .sr-card-name { font-size:.92rem; font-weight:700; color:#0f1123; margin-bottom:4px; }
    .sr-card-meta { font-size:.72rem; color:#9094a8; margin-bottom:10px; }
    .sr-card-pct  { font-size:1.4rem; font-weight:700; font-family:'JetBrains Mono',monospace; }
    .sr-card-bar  { height:5px; border-radius:99px; background:#f0f2f8; margin-top:8px; overflow:hidden; }
    .sr-card-fill { height:100%; border-radius:99px; transition:width .5s ease; }
    .sr-card-total{ font-size:.7rem; color:#9094a8; margin-top:4px; white-space:nowrap; }
    .sr-back { display:inline-flex; align-items:center; gap:6px; font-size:.78rem; font-weight:600; color:#6b7194; cursor:pointer; margin-bottom:1rem; padding:6px 0; }
    .sr-back:hover { color:#8b5cf6; }
    .sr-subject-hdr { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1.1rem 1.3rem; margin-bottom:1rem; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; }
    .sr-subject-title { font-size:1rem; font-weight:700; color:#0f1123; }
    .sr-subject-badges { display:flex; gap:8px; flex-wrap:wrap; }
    .sr-badge { font-size:.7rem; font-weight:600; padding:4px 10px; border-radius:99px; }
    .sr-assess-list { display:flex; flex-direction:column; gap:8px; }
    .sr-assess-card { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:12px 16px; display:flex; align-items:center; gap:12px; transition:.12s; }
    .sr-assess-card:hover { border-color:#8b5cf6; background:#fafbff; }
    .sr-assess-icon { width:38px; height:38px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:.72rem; font-weight:700; font-family:'JetBrains Mono',monospace; flex-shrink:0; }
    .sr-assess-info { flex:1; min-width:0; }
    .sr-assess-type { font-size:.82rem; font-weight:700; color:#0f1123; }
    .sr-assess-date { font-size:.7rem; color:#9094a8; margin-top:2px; font-family:'JetBrains Mono',monospace; }
    .sr-assess-right { text-align:right; flex-shrink:0; }
    .sr-assess-marks { font-size:1rem; font-weight:700; font-family:'JetBrains Mono',monospace; }
    .sr-assess-max   { font-size:.7rem; color:#9094a8; }
    .sr-assess-pct   { font-size:.68rem; font-weight:700; padding:2px 7px; border-radius:99px; margin-top:3px; display:inline-block; }
    .sr-overall-bar  { background:linear-gradient(135deg,#8b5cf6,#6366f1); border-radius:16px; padding:1.1rem 1.4rem; color:white; margin-bottom:1.2rem; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; }
    .sr-overall-left {}
    .sr-overall-label { font-size:.76rem; opacity:.8; margin-bottom:3px; }
    .sr-overall-pct   { font-size:2rem; font-weight:700; font-family:'JetBrains Mono',monospace; line-height:1; }
    .sr-overall-marks { font-size:.76rem; opacity:.7; margin-top:3px; white-space:nowrap; }
    .sr-overall-stats { display:flex; gap:16px; flex-wrap:wrap; }
    .sr-overall-stat  { text-align:center; }
    .sr-overall-stat-val { font-size:1rem; font-weight:700; }
    .sr-overall-stat-lbl { font-size:.62rem; opacity:.7; font-weight:600; text-transform:uppercase; letter-spacing:.05em; margin-top:1px; }
    @media (max-width:600px) { .sr-cards{grid-template-columns:1fr 1fr;} .sr-overall-bar{flex-direction:column;} }
  `;
 
  const subjectData = activeSubject
    ? data?.subjectResults?.find(s => s.subject === activeSubject)
    : null;
 
  // ── SUBJECT DETAIL VIEW ────────────────────────────────────────────────────
  if (activeSubject && subjectData) {
    const color = SUBJ_COLORS[data.subjectResults.findIndex(s=>s.subject===activeSubject) % SUBJ_COLORS.length];
    const isTerm = data.mode === 'term';
    return (
      <div className="sp"><style>{sharedStyle + resultsStyle}</style>
        <div className="sp-header no-print">
          <div>
            <div className="sr-back" onClick={()=>setActiveSubject(null)}>← All Subjects</div>
            <div className="sp-title">{activeSubject}</div>
            <div className="sp-sub">
              {`${year} · ${subjectData.entries?.length||0} assessment${subjectData.entries?.length!==1?'s':''}`}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={()=>window.print()}><PrinterIcon/>Print</button>
        </div>

        {/* Subject summary bar */}
        <div className="sr-subject-hdr">
          <div>
            <div className="sr-subject-title">{activeSubject}</div>
            {!isTerm && (
              <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:3}}>
                {subjectData.totalObtained} / {subjectData.totalMax} total marks
              </div>
            )}
          </div>
          <div className="sr-subject-badges">
            <span className="sr-badge" style={{background:pctBg(subjectData.percentage),color:pctColor(subjectData.percentage)}}>
              {isTerm ? 'Overall' : 'You'}: {subjectData.percentage ?? '—'}{subjectData.percentage != null ? '%' : ''}
            </span>
            {subjectData.highest != null && (
              <span className="sr-badge" style={{background:'#ecfdf5',color:'#10c98e'}}>
                Highest: {subjectData.highest}%
              </span>
            )}
            {subjectData.average != null && (
              <span className="sr-badge" style={{background:'#eef2ff',color:'#3b6ef8'}}>
                Avg: {subjectData.average}%
              </span>
            )}
          </div>
        </div>

        {/* Assessment list (regular + online assessments) */}
        <div className="sr-assess-list">
          {(subjectData.entries||[]).map((e, i) => {
            const online   = e.source === 'online';
            const pending  = !!e.pending;
            const obtained = e.isAbsent || e.isNA || pending ? null : e.marksObtained;
            const pct = obtained != null && e.maxMarks > 0
              ? Math.round((obtained / e.maxMarks) * 100) : null;
            const color2 = pct != null ? pctColor(pct) : '#9094a8';
            const bg2    = pct != null ? pctBg(pct)    : '#f0f2f7';
            return (
              <div key={`${e.source||'a'}-${e.assessmentId||''}-${i}`} className="sr-assess-card">
                <div className="sr-assess-icon" style={{background:`${color}18`,color}}>
                  {(e.typeName||'').slice(0,3).toUpperCase()}
                </div>
                <div className="sr-assess-info">
                  <div className="sr-assess-type">
                    {e.typeName}
                    {online && (
                      <span className="sr-badge" style={{background:'#eef2ff',color:'#3b6ef8',marginLeft:6}}>
                        {pending ? 'Online · awaiting final marks' : 'Online'}
                      </span>
                    )}
                  </div>
                  {e.description && <div style={{fontSize:'.72rem',color:'#6b7194',marginTop:1}}>{e.description}</div>}
                  {e.remarks && <div style={{fontSize:'.7rem',color:'#9094a8',marginTop:1,fontStyle:'italic'}}>{e.remarks}</div>}
                  <div className="sr-assess-date">
                    📅 {fmtAssessmentDate(e.date)}
                  </div>
                </div>
                <div className="sr-assess-right">
                  {pending ? (
                    <div style={{fontSize:'.72rem',fontWeight:700,color:'#f59e0b'}}>Pending</div>
                  ) : e.isAbsent ? (
                    <div style={{fontSize:'.82rem',fontWeight:700,color:'#ef4444'}}>Absent</div>
                  ) : e.isNA ? (
                    <div style={{fontSize:'.82rem',fontWeight:700,color:'#9094a8'}}>N/A</div>
                  ) : (
                    <>
                      <div style={{whiteSpace:'nowrap'}}>
                        <span className="sr-assess-marks" style={{color:color2}}>{obtained ?? '—'}</span>
                        <span className="sr-assess-max">/{e.maxMarks}</span>
                      </div>
                      {pct != null && (
                        <span className="sr-assess-pct" style={{background:bg2,color:color2}}>
                          {pct}%
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
 
  // ── SUBJECT CARD GRID VIEW ─────────────────────────────────────────────────
  const isTermMode = data?.mode === 'term';
  return (
    <div className="sp print-area"><style>{sharedStyle + resultsStyle}</style>
      <div className="sp-header no-print">
        <div>
          <div className="sp-title">My Results</div>
          <div className="sp-sub">Tap a subject to see detailed assessment results</div>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
          <input type="text" className="filter-input" value={year}
            onChange={e=>setYear(e.target.value)}
            placeholder="2025-2026" style={{width:130}}/>
          {data && <button className="btn btn-ghost btn-sm" onClick={()=>window.print()}><PrinterIcon/>Print</button>}
        </div>
      </div>
 
      {loading ? (
        <div className="sp-loading"><div className="spinner"/>Loading…</div>
      ) : !data?.subjectResults?.length ? (
        <div className="sp-empty">No results found for {year}.</div>
      ) : (
        <>
          {/* Overall performance banner */}
          <div className="sr-overall-bar">
            <div className="sr-overall-left">
              <div className="sr-overall-label">Overall Performance — {year}</div>
              <div className="sr-overall-pct">
                {data.overallPercentage ?? '—'}{data.overallPercentage != null ? '%' : ''}
              </div>
              {!isTermMode && (
                <div className="sr-overall-marks">
                  {data.grandTotalObtained} / {data.grandTotalMax} marks
                </div>
              )}
            </div>
            <div className="sr-overall-stats">
              <div className="sr-overall-stat">
                <div className="sr-overall-stat-val">{data.subjectResults.length}</div>
                <div className="sr-overall-stat-lbl">Subjects</div>
              </div>
              <div className="sr-overall-stat">
                <div className="sr-overall-stat-val">
                  {data.subjectResults.filter(s => s.percentage >= 60).length}
                </div>
                <div className="sr-overall-stat-lbl">Passed</div>
              </div>
              <div className="sr-overall-stat">
                <div className="sr-overall-stat-val">
                  {data.subjectResults.reduce((s,r) => s + (r.entries?.length||0), 0)}
                </div>
                <div className="sr-overall-stat-lbl">Assessments</div>
              </div>
            </div>
          </div>

          {/* Subject cards */}
          <div className="sr-cards">
            {data.subjectResults.map((subj, i) => {
              const color = SUBJ_COLORS[i % SUBJ_COLORS.length];
              const pct   = subj.percentage;
              return (
                <div key={subj.subject} className="sr-card"
                  onClick={() => setActiveSubject(subj.subject)}
                  style={{borderTop:`3px solid ${color}`}}>
                  <div className="sr-card-icon" style={{background:`${color}18`,color}}>
                    {subj.subject.slice(0,3).toUpperCase()}
                  </div>
                  <div className="sr-card-name">{subj.subject}</div>
                  <div className="sr-card-meta">
                    {`${subj.entries?.length||0} assessment${subj.entries?.length!==1?'s':''} · tap to view`}
                  </div>
                  <div className="sr-card-pct" style={{color: pct != null ? pctColor(pct) : '#c0c5dc'}}>
                    {pct ?? '—'}{pct != null ? '%' : ''}
                  </div>
                  {!isTermMode && (
                    <div className="sr-card-total">
                      {subj.totalObtained} / {subj.totalMax} marks
                    </div>
                  )}
                  <div className="sr-card-bar">
                    <div className="sr-card-fill"
                      style={{width:`${pct ?? 0}%`, background: pct != null ? pctColor(pct) : '#e0e4f0'}}/>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ─── FEES ─────────────────────────────────────────────────────────────────────
export const StudentFees = () => {
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [proofModal,   setProofModal]   = useState(null);
  const [proofFile,    setProofFile]    = useState(null);
  const [proofPreview, setProofPreview] = useState('');
  const [proofNote,    setProofNote]    = useState('');
  const [submitting,   setSubmitting]   = useState(false);

  const fetchFees=()=>{setLoading(true);api.get('/student/fees').then(r=>setData(r.data)).catch(()=>toast.error('Failed to load fees')).finally(()=>setLoading(false));};
  useEffect(()=>{fetchFees();},[]);

  const fmt=(n)=>`Rs. ${Number(n||0).toLocaleString('en-PK')}`;
  const fmtHostel=(n)=>`Rs. ${Number(n||0).toLocaleString('en-PK',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const fmtDate=(d)=>d?new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'}):'—';
  const monthLabel=(m)=>{ if(!m) return '—'; const [y,mn]=String(m).split('-').map(Number); return new Date(y,mn-1,1).toLocaleDateString('en-US',{month:'short',year:'numeric'}); };
  const statusColor={paid:'#10c98e',unpaid:'#ef4444',overdue:'#ea580c',waived:'#6b7280',pending_verification:'#9333ea'};
  const statusBg   ={paid:'#ecfdf5',unpaid:'#fef2f2',overdue:'#fff7ed',waived:'#f3f4f6',pending_verification:'#fdf4ff'};
  const statusLabel={paid:'✓ Paid',unpaid:'• Unpaid',overdue:'⚠ Overdue',waived:'Waived',pending_verification:'📎 Proof Submitted'};

  const handleFileSelect=(file)=>{
    if(!file)return;
    if(!file.type.startsWith('image/')){toast.error('Please select an image file');return;}
    if(file.size>4*1024*1024){toast.error('Image must be under 4MB');return;}
    setProofFile(file);
    const reader=new FileReader();
    reader.onload=(e)=>setProofPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmitProof=async()=>{
    if(!proofFile){toast.error('Please attach a payment screenshot');return;}
    setSubmitting(true);
    try{
      await api.put(`/student/fees/${proofModal._id}/proof`,{paymentProofBase64:proofPreview,paymentProofNote:proofNote});
      toast.success('Payment proof submitted!');
      setProofModal(null);setProofFile(null);setProofPreview('');setProofNote('');
      fetchFees();
    }catch(err){toast.error(err.response?.data?.message||'Failed to submit');}
    finally{setSubmitting(false);}
  };

  const printChallan=(rec)=>{
    const win=window.open('','_blank','width=700,height=600');
    win.document.write(`<!DOCTYPE html><html><head><title>Fee Challan</title><style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;color:#111;padding:20px}.challan{max-width:620px;margin:0 auto;border:2px solid #1a1d2e;border-radius:4px;overflow:hidden}.hdr{background:#1a1d2e;color:white;padding:14px 18px;display:flex;justify-content:space-between;align-items:center}.hdr-name{font-size:17px;font-weight:700}.hdr-badge{font-size:12px;background:rgba(255,255,255,.18);padding:5px 12px;border-radius:4px;font-weight:700}.body{padding:16px 18px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:14px}.cell{background:#f8f9fc;border-radius:4px;padding:8px 10px}.cell-key{font-size:9px;font-weight:700;color:#888;text-transform:uppercase}.cell-val{font-size:13px;font-weight:600}hr{border:none;border-top:1px solid #eee;margin:12px 0}table{width:100%;border-collapse:collapse;margin-bottom:14px}th{padding:8px 10px;text-align:left;font-size:10px;font-weight:700;color:#888;background:#f8f9fc;border-bottom:1px solid #eee}td{padding:8px 10px;font-size:12px;border-bottom:1px solid #f5f5f5}.tfoot td{font-weight:700;font-size:13px;background:#f0f9ff;border-top:2px solid #3b6ef8}.ftr{padding:8px 18px;background:#f8f9fc;font-size:10px;color:#888;text-align:center;border-top:1px solid #eee}@media print{body{padding:0}}</style></head><body>
    <div class="challan">
      <div class="hdr"><div class="hdr-name">Fee Challan</div><div class="hdr-badge">${rec.month}</div></div>
      <div class="body">
        <div class="grid">
          <div class="cell"><div class="cell-key">Student</div><div class="cell-val">${rec.student?.user?.name||'—'}</div></div>
          <div class="cell"><div class="cell-key">Admission No</div><div class="cell-val">${rec.student?.admissionNo||'—'}</div></div>
          <div class="cell"><div class="cell-key">Class / Section</div><div class="cell-val">${rec.class?.name||'—'} — Sec ${rec.section||'—'}</div></div>
          <div class="cell"><div class="cell-key">Due Date</div><div class="cell-val">${rec.dueDate?new Date(rec.dueDate).toLocaleDateString('en-PK',{day:'numeric',month:'long',year:'numeric'}):'—'}</div></div>
        </div>
        <hr/>
        <table>
          <thead><tr><th>Description</th><th style="text-align:right">Amount</th></tr></thead>
          <tbody>
            <tr><td>Tuition Fee</td><td style="text-align:right;font-family:monospace;font-weight:600">Rs. ${(rec.amount||0).toLocaleString('en-PK')}</td></tr>
            <tr><td>Hostel Fee</td><td style="text-align:right;font-family:monospace;font-weight:600">${fmtHostel(rec.hostelFee)}</td></tr>
            ${(rec.fines||[]).map(f=>`<tr><td style="color:#ea580c">Fine: ${f.reason}</td><td style="text-align:right;font-family:monospace;font-weight:600;color:#ea580c">+ Rs. ${(f.amount||0).toLocaleString('en-PK')}</td></tr>`).join('')}
            ${rec.discount>0?`<tr><td style="color:#10c98e">Discount</td><td style="text-align:right;font-family:monospace;font-weight:600;color:#10c98e">− Rs. ${(rec.discount||0).toLocaleString('en-PK')}</td></tr>`:''}
          </tbody>
          <tfoot><tr class="tfoot"><td>Total Due</td><td style="text-align:right;font-family:monospace">Rs. ${(rec.totalDue||0).toLocaleString('en-PK')}</td></tr></tfoot>
        </table>
        ${rec.status==='paid'?`<div style="background:#ecfdf5;border:1px solid #a7f3d0;border-radius:6px;padding:8px 12px;font-size:12px;color:#065f46;margin-bottom:10px">✓ Paid on ${new Date(rec.paidAt).toLocaleDateString('en-PK',{day:'numeric',month:'long',year:'numeric'})}</div>`:''}
      </div>
      <div class="ftr">Generated on ${new Date().toLocaleDateString('en-PK',{day:'numeric',month:'long',year:'numeric'})}</div>
    </div>
    <script>window.onload=()=>{window.print()}<\/script></body></html>`);
    win.document.close();
  };

  const feeStyle = `
    .sf * { box-sizing:border-box; }
    .sf { font-family:'Sora',sans-serif; color:#1a1d2e; animation:sfFade .4s ease both; }
    @keyframes sfFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
    .sf-title { font-size:1.25rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; margin-bottom:4px; }
    .sf-sub   { font-size:.76rem; color:#9094a8; margin-bottom:1.4rem; }
    .sf-stat-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:.8rem; margin-bottom:1.4rem; }
    .sf-stat { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:.9rem 1rem; }
    .sf-stat-label { font-size:.66rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-bottom:4px; }
    .sf-stat-val { font-size:1.1rem; font-weight:700; font-family:'JetBrains Mono',monospace; line-height:1; }
    .sf-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
    .sf-panel-head { padding:13px 18px; border-bottom:1px solid #f0f2f8; font-size:.86rem; font-weight:600; color:#1a1d2e; }
    .sf-bill { padding:14px 18px; border-bottom:1px solid #f3f5fb; }
    .sf-bill:last-child { border-bottom:none; }
    .sf-bill-top { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; flex-wrap:wrap; }
    .sf-bill-month { font-family:'JetBrains Mono',monospace; font-size:.84rem; font-weight:700; color:#0f1123; }
    .sf-bill-meta  { font-size:.72rem; color:#9094a8; margin-top:3px; }
    .sf-bill-right { display:flex; align-items:center; gap:8px; flex-shrink:0; flex-wrap:wrap; }
    .sf-amount { font-family:'JetBrains Mono',monospace; font-size:.9rem; font-weight:700; color:#0f1123; }
    .sf-badge { display:inline-flex; align-items:center; font-size:.68rem; font-weight:600; padding:3px 10px; border-radius:99px; white-space:nowrap; }
    .sf-breakdown { margin-top:8px; padding:8px 12px; background:#f8f9fc; border-radius:8px; font-size:.76rem; }
    .sf-fine-row { color:#ea580c; }
    .sf-disc-row { color:#10c98e; }
    .sf-actions { display:flex; gap:6px; margin-top:10px; flex-wrap:wrap; }
    .sf-btn { display:inline-flex; align-items:center; gap:5px; font-size:.74rem; font-weight:600; padding:5px 11px; border-radius:8px; border:1.5px solid; cursor:pointer; font-family:'Sora',sans-serif; transition:all .15s; background:white; }
    .sf-btn-pay   { border-color:#3b6ef8; color:#3b6ef8; }
    .sf-btn-pay:hover { background:#eef2ff; }
    .sf-btn-print { border-color:#8b5cf6; color:#8b5cf6; }
    .sf-btn-print:hover { background:#faf5ff; }
    .sf-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
    .sf-loading { display:flex; align-items:center; justify-content:center; height:160px; gap:10px; color:#9094a8; }
    .spinner { width:16px; height:16px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
    @keyframes spin { to{transform:rotate(360deg);} }
    .modal-backdrop { position:fixed; inset:0; background:rgba(10,12,28,.55); backdrop-filter:blur(3px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
    .modal-box { background:white; border-radius:18px; width:100%; max-width:440px; border:1px solid #e8ebf4; box-shadow:0 20px 60px rgba(0,0,0,.2); overflow:hidden; display:flex; flex-direction:column; max-height:90vh; }
    .modal-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-shrink:0; }
    .modal-title-sm { font-size:.88rem; font-weight:700; color:#0f1123; }
    .modal-close-sm { width:26px; height:26px; border-radius:7px; border:1px solid #e8ebf4; background:#f8f9fc; display:flex; align-items:center; justify-content:center; cursor:pointer; font-size:1rem; color:#9094a8; }
    .modal-body { padding:16px 18px; overflow-y:auto; flex:1; }
    .modal-foot { display:flex; justify-content:flex-end; gap:8px; padding:12px 18px; border-top:1px solid #f0f2f8; flex-shrink:0; flex-wrap:wrap; }
    .f-field { display:flex; flex-direction:column; gap:4px; margin-bottom:12px; }
    .f-label { font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
    .f-input { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
    .f-input:focus { border-color:#3b6ef8; background:white; }
    .drop-zone { border:2px dashed #d0d5e8; border-radius:12px; padding:22px 16px; text-align:center; cursor:pointer; transition:all .15s; background:#fafbfc; }
    .drop-zone:hover { border-color:#3b6ef8; background:#f0f4ff; }
    .drop-zone-text { font-size:.82rem; font-weight:600; color:#1a1d2e; margin-top:4px; }
    .drop-zone-sub  { font-size:.72rem; color:#9094a8; margin-top:3px; }
    .proof-preview { max-width:100%; border-radius:10px; border:1px solid #e8ebf4; margin-top:8px; display:block; }
    .btn-submit { background:#3b6ef8; color:white; border:none; }
    .btn-submit:hover { background:#2d5ef0; }
    .btn-cancel-sm { background:#f0f2f7; color:#6b7194; border:none; }
    @media (max-width:768px) {
      .sf-stat-grid { grid-template-columns:1fr 1fr; }
      .sf-actions { flex-direction:column; }
      .sf-btn { justify-content:center; }
      .modal-foot { justify-content:stretch; }
      .modal-foot .sf-btn { flex:1; justify-content:center; }
    }
  `;

  return (
    <div className="sf">
      <style>{feeStyle}</style>
      <div className="sf-title">My Fees</div>
      <div className="sf-sub">View your monthly fee bills · print · submit payment proof</div>

      {loading ? (
        <div className="sf-loading"><div className="spinner"/>Loading…</div>
      ) : !data ? null : (
        <>
          <div className="sf-stat-grid">
            <div className="sf-stat"><div className="sf-stat-label">Total Paid</div><div className="sf-stat-val" style={{color:'#10c98e'}}>{fmt(data.summary?.paid)}</div></div>
            <div className="sf-stat"><div className="sf-stat-label">Pending</div><div className="sf-stat-val" style={{color:data.summary?.pending>0?'#ef4444':'#10c98e'}}>{fmt(data.summary?.pending)}</div></div>
            <div className="sf-stat"><div className="sf-stat-label">Overdue</div><div className="sf-stat-val" style={{color:data.summary?.overdue>0?'#ea580c':'#10c98e'}}>{data.summary?.overdue} bill{data.summary?.overdue!==1?'s':''}</div></div>
          </div>
          <div className="sf-panel">
            <div className="sf-panel-head">Fee Bills ({data.records?.length})</div>
            {data.records?.length===0 ? (
              <div className="sf-empty">No fee bills yet.</div>
            ) : data.records?.map(rec=>{
              const canSubmitProof=!['paid','waived','pending_verification'].includes(rec.status);
              return (
                <div key={rec._id} className="sf-bill">
                  <div className="sf-bill-top">
                    <div>
                      <div className="sf-bill-month">{monthLabel(rec.month)}</div>
                      <div className="sf-bill-meta">{rec.class?.name}{rec.dueDate&&` · Due: ${fmtDate(rec.dueDate)}`}</div>
                    </div>
                    <div className="sf-bill-right">
                      <div style={{textAlign:'right'}}>
                        <div className="sf-amount">{fmt(rec.totalDue)}</div>
                        {rec.status==='paid'&&<div style={{fontSize:'.68rem',color:'#9094a8'}}>Paid {fmtDate(rec.paidAt)}{rec.receiptNo?` · #${rec.receiptNo}`:''}</div>}
                      </div>
                      <span className="sf-badge" style={{background:statusBg[rec.status],color:statusColor[rec.status]}}>{statusLabel[rec.status]}</span>
                    </div>
                  </div>
                  {(
                    <div className="sf-breakdown">
                      <div style={{display:'flex',justifyContent:'space-between',color:'#6b7194',marginBottom:3}}><span>Base Fee</span><span style={{fontFamily:'monospace'}}>{fmt(rec.amount)}</span></div>
                      <div style={{display:'flex',justifyContent:'space-between',color:'#6b7194',marginBottom:3}}><span>Hostel Fee</span><span style={{fontFamily:'monospace'}}>{fmtHostel(rec.hostelFee)}</span></div>
                      {(rec.fines||[]).map(f=><div key={f._id} className="sf-fine-row" style={{display:'flex',justifyContent:'space-between',marginBottom:2}}><span>Fine: {f.reason}</span><span style={{fontFamily:'monospace'}}>+ {fmt(f.amount)}</span></div>)}
                      {rec.discount>0&&<div className="sf-disc-row" style={{display:'flex',justifyContent:'space-between',marginBottom:2}}><span>Discount</span><span style={{fontFamily:'monospace'}}>− {fmt(rec.discount)}</span></div>}
                      <div style={{display:'flex',justifyContent:'space-between',fontWeight:700,borderTop:'1px solid #eee',paddingTop:4,marginTop:4}}><span>Total</span><span style={{fontFamily:'monospace'}}>{fmt(rec.totalDue)}</span></div>
                    </div>
                  )}
                  {rec.remarks&&<div style={{marginTop:6,fontSize:'.72rem',color:'#3b6ef8'}}>📌 {rec.remarks}</div>}
                  <div className="sf-actions">
                    {canSubmitProof&&<button className="sf-btn sf-btn-pay" onClick={()=>{setProofModal(rec);setProofFile(null);setProofPreview('');setProofNote('');}}>📎 Submit Proof</button>}
                    <button className="sf-btn sf-btn-print" onClick={()=>printChallan(rec)}>🖨 Print</button>
                  </div>
                  {rec.status==='pending_verification'&&<div style={{marginTop:8,fontSize:'.74rem',color:'#9333ea',background:'#fdf4ff',border:'1px solid #e9d5ff',borderRadius:7,padding:'5px 10px',display:'inline-block'}}>📎 Proof submitted — admin will verify soon</div>}
                </div>
              );
            })}
          </div>
        </>
      )}

      {proofModal&&(
        <div className="modal-backdrop" onClick={()=>!submitting&&setProofModal(null)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title-sm">Submit Payment Proof</div><button className="modal-close-sm" onClick={()=>!submitting&&setProofModal(null)} disabled={submitting}>✕</button></div>
            <div className="modal-body">
              <div style={{background:'#f8f9fc',border:'1px solid #e8ebf4',borderRadius:9,padding:'10px 12px',marginBottom:14}}>
                <div style={{fontWeight:700,fontSize:'.88rem',color:'#0f1123'}}>{proofModal.month} — {fmt(proofModal.totalDue)}</div>
                <div style={{fontSize:'.76rem',color:'#6b7194',marginTop:2}}>{proofModal.class?.name} · Take a clear screenshot of your payment</div>
              </div>
              <div className="f-field">
                <label className="f-label">Payment Screenshot *</label>
                <div className="drop-zone" onClick={()=>document.getElementById('proof-file-input').click()}
                  onDragOver={e=>{e.preventDefault();}} onDrop={e=>{e.preventDefault();handleFileSelect(e.dataTransfer.files[0]);}}>
                  <input id="proof-file-input" type="file" accept="image/*" style={{display:'none'}} onChange={e=>handleFileSelect(e.target.files[0])} />
                  {proofPreview ? (
                    <div><img src={proofPreview} alt="proof" className="proof-preview" /><div style={{fontSize:'.72rem',color:'#3b6ef8',marginTop:6}}>✓ {proofFile?.name} — tap to change</div></div>
                  ) : (
                    <div><div style={{fontSize:'1.8rem',marginBottom:4}}>📸</div><div className="drop-zone-text">Tap to select screenshot</div><div className="drop-zone-sub">JPG, PNG, WEBP · max 4MB</div></div>
                  )}
                </div>
              </div>
              <div className="f-field">
                <label className="f-label">Note (optional)</label>
                <input type="text" className="f-input" value={proofNote} onChange={e=>setProofNote(e.target.value)} placeholder="e.g. Paid via JazzCash to 0300-..." />
              </div>
            </div>
            <div className="modal-foot">
              <button className="sf-btn btn-cancel-sm" onClick={()=>setProofModal(null)} disabled={submitting}>Cancel</button>
              <button className="sf-btn btn-submit" onClick={handleSubmitProof} disabled={submitting||!proofFile}>{submitting?'Uploading…':'Submit Proof'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── MATERIALS ────────────────────────────────────────────────────────────────
export const StudentMaterials = () => {
  const SUBJ_COLORS = ['#3b6ef8','#10c98e','#8b5cf6','#f59e0b','#ef4444','#06b6d4','#ec4899','#14b8a6'];
  const TYPE_ICON   = {pdf:'📄',doc:'📝',ppt:'📊',image:'🖼️',video:'🎬',youtube:'▶️',other:'📎'};
  const TYPE_COLOR  = {pdf:'#ef4444',doc:'#3b6ef8',ppt:'#f59e0b',image:'#10c98e',video:'#8b5cf6',youtube:'#ef4444',other:'#6b7194'};

  const [navStep,       setNavStep]       = useState('subjects');
  const [activeSubject, setActiveSubject] = useState('');
  const [filterType,    setFilterType]    = useState('');
  const [materials,     setMaterials]     = useState([]);
  const [loading,       setLoading]       = useState(true);

  const [practiceModal,   setPracticeModal]   = useState(null);
  const [practiceQuiz,    setPracticeQuiz]    = useState(null);
  const [practiceView,    setPracticeView]    = useState('setup');
  const [practiceForm,    setPracticeForm]    = useState({numQuestions:'10',difficulty:'medium'});
  const [practiceAnswers, setPracticeAnswers] = useState({});
  const [practiceResult,  setPracticeResult]  = useState(null);
  const [generating,      setGenerating]      = useState(false);
  const [practiceError,   setPracticeError]   = useState('');

  useEffect(()=>{
    api.get('/student/materials').then(r=>setMaterials(r.data))
      .catch(()=>toast.error('Failed to load materials')).finally(()=>setLoading(false));
  },[]);

  const allGrouped = materials.reduce((acc,m)=>{ const key=m.subject||'General'; if(!acc[key])acc[key]=[]; acc[key].push(m); return acc; },{});
  const subjects   = Object.keys(allGrouped);
  const subjectMaterials = activeSubject ? (allGrouped[activeSubject]||[]).filter(m=>!filterType||m.type===filterType) : [];

  const handleGeneratePractice=async()=>{
    if(!practiceModal)return; setGenerating(true); setPracticeView('loading');
    try{
      let docText=''; let extractOk=false;
      if(practiceModal.fileUrl){
        try{const{data:extracted}=await api.post('/student/quizzes/extract-from-url',{fileUrl:practiceModal.fileUrl,fileName:practiceModal.title,type:practiceModal.type});
          if(extracted.text&&extracted.text.trim().length>50){docText=extracted.text;extractOk=true;}
        }catch{toast('Could not read file, generating from title…',{icon:'⚠️'});}
      }
      if(!extractOk){
        docText=[practiceModal.title,practiceModal.subject,practiceModal.description].filter(Boolean).join('. ');
        if(docText.length<30){toast.error('Not enough content.');setPracticeView('setup');return;}
      }
      const{data}=await api.post('/student/quizzes/practice',{text:docText,numQuestions:Number(practiceForm.numQuestions)||10,difficulty:practiceForm.difficulty,title:practiceModal.title});
      if(!data.questions||data.questions.length===0){toast.error('AI could not generate questions.');setPracticeView('setup');return;}
      setPracticeQuiz(data);setPracticeAnswers({});setPracticeResult(null);setPracticeView('attempt');
    }catch(err){toast.error(err.response?.data?.message||'Failed to generate quiz.');setPracticeView('setup');}
    finally{setGenerating(false);}
  };

  const submitPractice=()=>{
    const unanswered=practiceQuiz.questions.filter((q,i)=>practiceAnswers[i]===undefined).length;
    if(unanswered>0){setPracticeError(`${unanswered} question${unanswered!==1?'s':''} remaining — attempt the full quiz first`);return;}
    setPracticeError('');
    let correct=0;
    const results=practiceQuiz.questions.map((q,i)=>{const selected=practiceAnswers[i];const isCorrect=selected===q.correctIndex;if(isCorrect)correct++;return{...q,selectedIndex:selected,isCorrect};});
    const total=practiceQuiz.questions.length||1;
    setPracticeResult({results,score:correct,total:practiceQuiz.questions.length,percentage:Math.round((correct/total)*100)});
    setPracticeView('result');
  };

  const closePractice=()=>{setPracticeModal(null);setPracticeView('setup');setPracticeQuiz(null);setPracticeResult(null);setPracticeAnswers({});setPracticeError('');};

  // ── MATERIAL LIST ───────────────────────────────────────────────────────────
  if (navStep === 'list') return (
    <div className="sp"><style>{sharedStyle}</style>
      {/* Breadcrumb */}
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:'1.2rem',flexWrap:'wrap'}}>
        <span style={{fontSize:'.78rem',fontWeight:600,color:'#9094a8',cursor:'pointer'}}
          onClick={()=>{setNavStep('subjects');setActiveSubject('');setFilterType('');}}>Materials</span>
        <span style={{color:'#d1d5db'}}>›</span>
        <span style={{fontSize:'.78rem',fontWeight:600,color:'#0f1123'}}>{activeSubject}</span>
      </div>

      <div className="sp-header">
        <div><div className="sp-title">{activeSubject}</div><div className="sp-sub">{subjectMaterials.length} material{subjectMaterials.length!==1?'s':''}</div></div>
      </div>

      {/* Type filter */}
      <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:'1rem',overflowX:'auto',paddingBottom:2}}>
        {['','doc','image','other','pdf','ppt','video','youtube'].map(t=>(
          <button key={t} onClick={()=>setFilterType(t)}
            style={{padding:'5px 12px',borderRadius:8,border:'1.5px solid',fontFamily:"'Sora',sans-serif",fontSize:'.74rem',fontWeight:600,cursor:'pointer',whiteSpace:'nowrap',flexShrink:0,borderColor:filterType===t?'#8b5cf6':'#e0e4f0',background:filterType===t?'#8b5cf6':'white',color:filterType===t?'white':'#6b7194'}}>
            {t?(TYPE_ICON[t]||'📎')+' '+t.toUpperCase():'All'}
          </button>
        ))}
      </div>

      <div className="panel">
        <div className="panel-head"><div className="panel-title">Materials ({subjectMaterials.length})</div></div>
        {subjectMaterials.length===0 ? <div className="sp-empty">No {filterType||''} materials for {activeSubject} yet.</div> : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:10,padding:10}}>
            {subjectMaterials.map(m=>(
              <div key={m._id} style={{borderRadius:12,border:'1px solid #edf0f9',padding:'12px 14px',background:'#fafbff',display:'flex',flexDirection:'column',gap:6,transition:'all .15s'}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor='#8b5cf6';e.currentTarget.style.background='white';}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor='#edf0f9';e.currentTarget.style.background='#fafbff';}}>
                <div style={{display:'flex',alignItems:'flex-start',gap:9}}>
                  <div style={{width:30,height:30,borderRadius:8,background:`${TYPE_COLOR[m.type]||'#6b7194'}18`,color:TYPE_COLOR[m.type]||'#6b7194',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.82rem',flexShrink:0}}>{TYPE_ICON[m.type]||'📎'}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:'.82rem',fontWeight:600,color:'#0f1123',lineHeight:1.35}}>{m.title}</div>
                    <div style={{fontSize:'.68rem',color:'#9094a8'}}>{m.uploadedBy?.name&&`${m.uploadedBy.name} · `}{m.createdAt?new Date(m.createdAt).toLocaleDateString('en-US',{day:'numeric',month:'short'}):''}</div>
                  </div>
                </div>
                {m.description&&<div style={{fontSize:'.72rem',color:'#6b7194',lineHeight:1.45}}>{m.description}</div>}
                <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:2}}>
                  {(m.fileUrl||m.youtubeUrl)&&(
                    <a href={m.fileUrl?materialDownloadUrl(m):m.youtubeUrl} target="_blank" rel="noreferrer"
                      style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:'.72rem',fontWeight:600,padding:'4px 10px',borderRadius:7,border:'1.5px solid #3b6ef8',color:'#3b6ef8',background:'white',textDecoration:'none',cursor:'pointer'}}>
                      {m.type==='youtube'?'▶ Watch':m.type==='video'?'▶ Play':m.type==='image'?'🔍 View':'⬇ Download'}
                    </a>
                  )}
                  {['pdf','doc','docx','txt'].includes(m.type)&&<button onClick={()=>{setPracticeModal(m);setPracticeView('setup');}} style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:'.72rem',fontWeight:600,padding:'4px 10px',borderRadius:7,border:'1.5px solid #8b5cf6',color:'#8b5cf6',background:'white',cursor:'pointer'}}>🤖 Practice</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {practiceModal&&ReactDOM.createPortal((
        <div style={{position:'fixed',inset:0,background:'rgba(10,12,28,.55)',backdropFilter:'blur(3px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:99999,padding:20}}>
          <div style={{background:'white',borderRadius:18,width:'100%',maxWidth:480,maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,.2)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 18px',borderBottom:'1px solid #f0f2f8',flexShrink:0}}>
              <div style={{fontWeight:700,fontSize:'.9rem',color:'#0f1123'}}>🤖 Practice Quiz</div>
              <button onClick={closePractice} style={{width:26,height:26,borderRadius:7,border:'1px solid #e8ebf4',background:'#f8f9fc',cursor:'pointer',color:'#9094a8',fontSize:'1rem',display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
            </div>
            <div style={{flex:1,overflowY:'auto',padding:'16px 18px'}}>
              {practiceView==='setup'&&(<>
                <div style={{background:'#f3f0ff',border:'1px solid #ddd6fe',borderRadius:9,padding:'9px 12px',fontSize:'.78rem',color:'#7c3aed',marginBottom:14}}>📄 <strong>{practiceModal.title}</strong></div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
                  <div style={{display:'flex',flexDirection:'column',gap:4}}><label style={{fontSize:'.67rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase'}}>Questions</label><input type="number" min={1} max={20} value={practiceForm.numQuestions} onChange={e=>setPracticeForm({...practiceForm,numQuestions:e.target.value})} style={{fontFamily:'Sora,sans-serif',fontSize:'.82rem',padding:'8px 10px',border:'1.5px solid #e0e4f0',borderRadius:9,background:'#f8f9fc',outline:'none'}} /></div>
                  <div style={{display:'flex',flexDirection:'column',gap:4}}><label style={{fontSize:'.67rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase'}}>Difficulty</label><select value={practiceForm.difficulty} onChange={e=>setPracticeForm({...practiceForm,difficulty:e.target.value})} style={{fontFamily:'Sora,sans-serif',fontSize:'.82rem',padding:'8px 10px',border:'1.5px solid #e0e4f0',borderRadius:9,background:'#f8f9fc',outline:'none'}}><option value="easy">🟢 Easy</option><option value="medium">🟡 Medium</option><option value="hard">🔴 Hard</option></select></div>
                </div>
                <button onClick={handleGeneratePractice} style={{background:'#8b5cf6',color:'white',border:'none',borderRadius:10,padding:'10px 18px',fontFamily:'Sora,sans-serif',fontSize:'.82rem',fontWeight:600,cursor:'pointer',width:'100%'}}>🤖 Generate Practice Quiz</button>
              </>)}
              {practiceView==='loading'&&(<div style={{textAlign:'center',padding:'30px 0'}}><div style={{fontSize:'2rem',marginBottom:10}}>🤖</div><div style={{fontWeight:600,fontSize:'.88rem',color:'#1a1d2e',marginBottom:4}}>Generating questions…</div><div style={{fontSize:'.74rem',color:'#9094a8'}}>AI is reading the document</div></div>)}
              {practiceView==='attempt'&&practiceQuiz&&(<>
                <div style={{fontSize:'.74rem',color:'#9094a8',marginBottom:12}}>{practiceQuiz.questions?.length} questions · {practiceForm.difficulty}</div>
                {practiceQuiz.questions?.map((q,i)=>(
                  <div key={i} style={{background:'#f8f9fc',borderRadius:12,border:'1px solid #e8ebf4',padding:'12px 14px',marginBottom:8}}>
                    <div style={{fontSize:'.68rem',fontWeight:700,color:'#8b5cf6',textTransform:'uppercase',marginBottom:5}}>Q{i+1}</div>
                    <div style={{fontSize:'.84rem',fontWeight:600,color:'#0f1123',marginBottom:9,lineHeight:1.5}}>{q.question}</div>
                    {q.options?.map((opt,j)=>(<button key={j} onClick={()=>{setPracticeAnswers(p=>({...p,[i]:j}));setPracticeError('');}} style={{width:'100%',padding:'8px 12px',borderRadius:9,border:`2px solid ${practiceAnswers[i]===j?'#8b5cf6':'#e0e4f0'}`,background:practiceAnswers[i]===j?'#f3f0ff':'white',fontFamily:'Sora,sans-serif',fontSize:'.79rem',textAlign:'left',cursor:'pointer',marginBottom:5,display:'flex',alignItems:'center',gap:8,color:practiceAnswers[i]===j?'#7c3aed':'#1a1d2e',fontWeight:practiceAnswers[i]===j?600:400}}><span style={{width:22,height:22,borderRadius:99,background:practiceAnswers[i]===j?'#8b5cf6':'#f0f2f7',color:practiceAnswers[i]===j?'white':'#3b6ef8',fontSize:'.66rem',fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{String.fromCharCode(65+j)}</span>{opt}</button>))}
                  </div>
                ))}
                {practiceError&&<div style={{textAlign:'center',background:'#fef2f2',border:'1px solid #fecaca',color:'#ef4444',borderRadius:10,padding:'10px 14px',fontSize:'.8rem',fontWeight:600,marginBottom:10}}>⚠ {practiceError}</div>}
                <button onClick={submitPractice} style={{background:'#10c98e',color:'white',border:'none',borderRadius:10,padding:'10px 18px',fontFamily:'Sora,sans-serif',fontSize:'.82rem',fontWeight:600,cursor:'pointer',width:'100%',marginTop:4}}>✓ Check Answers</button>
              </>)}
              {practiceView==='result'&&practiceResult&&(<>
                <div style={{textAlign:'center',padding:'14px 0 18px'}}><div style={{fontSize:'2.2rem',fontWeight:700,fontFamily:'JetBrains Mono,monospace',color:practiceResult.percentage>=80?'#10c98e':practiceResult.percentage>=50?'#f59e0b':'#ef4444'}}>{practiceResult.percentage}%</div><div style={{fontSize:'.82rem',color:'#9094a8',marginTop:4}}>{practiceResult.score}/{practiceResult.total} correct</div></div>
                {practiceResult.results?.map((q,i)=>(<div key={i} style={{borderRadius:10,border:`1px solid ${q.isCorrect?'#a7f3d0':'#fecaca'}`,background:q.isCorrect?'#ecfdf5':'#fef2f2',padding:'10px 12px',marginBottom:7}}><div style={{fontSize:'.82rem',fontWeight:600,color:'#0f1123',marginBottom:5}}>{i+1}. {q.question}</div>{q.selectedIndex!==undefined?<div style={{fontSize:'.76rem',color:q.isCorrect?'#10c98e':'#ef4444'}}>{q.isCorrect?'✓':'✗'} {String.fromCharCode(65+q.selectedIndex)}. {q.options?.[q.selectedIndex]}</div>:<div style={{fontSize:'.76rem',color:'#9094a8'}}>Skipped</div>}{!q.isCorrect&&<div style={{fontSize:'.74rem',color:'#10c98e',marginTop:3}}>✓ {String.fromCharCode(65+q.correctIndex)}. {q.options?.[q.correctIndex]}</div>}{q.explanation&&<div style={{fontSize:'.71rem',color:'#6b7194',marginTop:5,background:'white',borderRadius:6,padding:'4px 8px',borderLeft:'2px solid #3b6ef8'}}>💡 {q.explanation}</div>}</div>))}
                <button onClick={()=>setPracticeView('setup')} style={{background:'#8b5cf6',color:'white',border:'none',borderRadius:10,padding:'9px 18px',fontFamily:'Sora,sans-serif',fontSize:'.82rem',fontWeight:600,cursor:'pointer',width:'100%',marginTop:8}}>Try Again</button>
              </>)}
            </div>
          </div>
        </div>
      ),document.body)}
    </div>
  );

  // ── SUBJECT CARDS ───────────────────────────────────────────────────────────
  return (
    <div className="sp"><style>{sharedStyle}</style>
      <div className="sp-header"><div><div className="sp-title">My Materials</div><div className="sp-sub">Study materials organized by subject</div></div></div>
      {loading ? <div className="sp-loading"><div className="spinner"/>Loading…</div>
      : subjects.length === 0 ? (
        <div className="sp-empty"><div style={{fontSize:'2.5rem',marginBottom:10}}>📚</div><div style={{fontWeight:700,fontSize:'.96rem',color:'#0f1123',marginBottom:6}}>No materials yet</div><div style={{fontSize:'.78rem',color:'#9094a8'}}>Your teachers haven't uploaded any materials yet.</div></div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))',gap:'1rem'}}>
          {subjects.map((subj,i) => {
            const count = allGrouped[subj].length;
            const color = SUBJ_COLORS[i%SUBJ_COLORS.length];
            const typeBreakdown = allGrouped[subj].reduce((acc,m)=>{ acc[m.type]=(acc[m.type]||0)+1; return acc; },{});
            return (
              <div key={subj} onClick={()=>{ setActiveSubject(subj); setNavStep('list'); setFilterType(''); }}
                style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'1.1rem 1.2rem',cursor:'pointer',transition:'all .15s'}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=color;e.currentTarget.style.transform='translateY(-1px)';}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor='#e8ebf4';e.currentTarget.style.transform='none';}}>
                <div style={{width:36,height:36,borderRadius:10,background:`${color}18`,color,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.8rem',marginBottom:8}}>{subj.slice(0,3).toUpperCase()}</div>
                <div style={{fontSize:'.92rem',fontWeight:700,color:'#0f1123',marginBottom:3}}>{subj}</div>
                <div style={{fontSize:'.72rem',color:'#9094a8'}}>{count} material{count!==1?'s':''}</div>
                <div style={{display:'flex',gap:4,flexWrap:'wrap',marginTop:6}}>
                  {Object.entries(typeBreakdown).slice(0,3).map(([type,cnt])=>(
                    <span key={type} style={{fontSize:'.63rem',fontWeight:600,padding:'2px 6px',borderRadius:99,background:`${TYPE_COLOR[type]||'#6b7194'}14`,color:TYPE_COLOR[type]||'#6b7194'}}>{TYPE_ICON[type]} {cnt}</span>
                  ))}
                </div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:10}}>
                  <span style={{fontSize:'.72rem',color,fontWeight:600}}>View materials</span>
                  <span style={{color,fontSize:'.9rem'}}>›</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ─── NOTICES ─────────────────────────────────────────────────────────────────
export const StudentNotices = () => {
  const [notices,setNotices]=useState([]);
  const [loading,setLoading]=useState(true);
  useEffect(()=>{api.get('/student/notices').then(r=>setNotices(r.data)).catch(()=>toast.error('Failed')).finally(()=>setLoading(false));},[]);
  const roleColor=(role)=>role==='admin'?'#3b6ef8':role==='teacher'?'#10c98e':'#6b7194';
  return (
    <div className="sp"><style>{sharedStyle}</style>
      <div className="sp-header"><div><div className="sp-title">Notices</div><div className="sp-sub">Announcements from admin and teachers</div></div></div>
      {loading?<div className="sp-loading"><div className="spinner"/>Loading…</div>
      :!notices.length?<div className="sp-empty">No notices yet.</div>
      :(
        <div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
          {notices.map(n=>(
            <div key={n._id} style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'1.1rem 1.3rem'}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10,marginBottom:8,flexWrap:'wrap'}}>
                <div style={{fontWeight:700,fontSize:'.9rem',color:'#0f1123',flex:1}}>{n.title}</div>
                <span style={{fontSize:'.67rem',fontWeight:600,padding:'3px 9px',borderRadius:99,background:`${roleColor(n.postedBy?.role)}18`,color:roleColor(n.postedBy?.role),whiteSpace:'nowrap',flexShrink:0}}>{n.postedBy?.role}</span>
              </div>
              <div style={{fontSize:'.82rem',color:'#4a5075',lineHeight:1.6,marginBottom:10}}>{n.content}</div>
              <div style={{fontSize:'.7rem',color:'#b0b5cc'}}>{n.postedBy?.name} · {fmtDate(n.createdAt)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── DATESHEETS ────────────────────────────────────────────────────────────────
export const StudentDatesheets = () => {
  const [datesheets,setDatesheets]=useState([]);
  const [loading,setLoading]=useState(true);
  useEffect(()=>{api.get('/student/datesheets').then(r=>setDatesheets(r.data)).catch(()=>toast.error('Failed')).finally(()=>setLoading(false));},[]);
  return (
    <div className="sp"><style>{sharedStyle}</style>
      <div className="sp-header"><div><div className="sp-title">Exam Datesheets</div><div className="sp-sub">View scheduled exam timetables</div></div></div>
      {loading?<div className="sp-loading"><div className="spinner"/>Loading…</div>
      :!datesheets.length?<div className="sp-empty">No datesheets published yet.</div>
      :(
        <div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
          {datesheets.map(d=>(
            <div key={d._id} style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'1.1rem 1.3rem'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,marginBottom:10,flexWrap:'wrap'}}>
                <div><div style={{fontWeight:700,fontSize:'.9rem',color:'#0f1123'}}>{d.title}</div><div style={{fontSize:'.72rem',color:'#9094a8',marginTop:3}}>{d.academicYear}{d.class?.name&&` · ${d.class.name}`} · {fmtDate(d.createdAt)}</div></div>
                {d.fileUrl&&<a href={d.fileUrl} target="_blank" rel="noreferrer" className="btn btn-purple btn-sm" style={{textDecoration:'none'}}>View PDF</a>}
              </div>
              {d.type==='manual'&&d.entries?.length>0&&(
                <>
                <div className="sp-tbl-wrap" style={{overflowX:'auto',WebkitOverflowScrolling:'touch'}}>
                  <table className="sp-table">
                    <thead><tr><th>Subject</th><th>Date</th><th>Time</th><th>Duration</th></tr></thead>
                    <tbody>{d.entries.map((e,i)=>(
                      <tr key={i}><td style={{fontWeight:600}}>{e.subject}</td><td><span className="mono">{fmtDate(e.date)}</span></td><td><span className="mono">{e.time||'—'}</span></td><td><span className="mono">{e.duration||'—'}</span></td></tr>
                    ))}</tbody>
                  </table>
                </div>
                <div className="sp-cards">
                  {d.entries.map((e,i)=>(
                    <div key={i} className="sp-card">
                      <div className="sp-card-top"><div style={{fontWeight:700,fontSize:'.84rem',color:'#0f1123'}}>{e.subject}</div><span className="mono">{fmtDate(e.date)}</span></div>
                      <div className="sp-card-row"><span className="sp-card-label">Time</span><span className="mono">{e.time||'—'}</span></div>
                      <div className="sp-card-row"><span className="sp-card-label">Duration</span><span className="mono">{e.duration||'—'}</span></div>
                    </div>
                  ))}
                </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── PROFILE ──────────────────────────────────────────────────────────────────
export const StudentProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pwSaving,setPwSaving]= useState(false);
  const [pwForm,  setPwForm]  = useState({currentPassword:'',newPassword:'',confirm:''});

  useEffect(()=>{api.get('/student/profile').then(r=>{setProfile(r.data);}).catch(()=>toast.error('Failed')).finally(()=>setLoading(false));},[]);

  const handleChangePw=async()=>{
    if(!pwForm.currentPassword||!pwForm.newPassword){toast.error('Fill all fields');return;}
    if(pwForm.newPassword!==pwForm.confirm){toast.error('Passwords do not match');return;}
    try{setPwSaving(true);await api.put('/student/profile/password',{currentPassword:pwForm.currentPassword,newPassword:pwForm.newPassword});toast.success('Password changed');setPwForm({currentPassword:'',newPassword:'',confirm:''});}
    catch(e){toast.error(e.response?.data?.message||'Failed');}finally{setPwSaving(false);}
  };

  if(loading)return<div className="sp"><style>{sharedStyle}</style><div className="sp-loading"><div className="spinner"/>Loading…</div></div>;
  const initials=(n='')=>n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';

  const profileStyle=`
    @media (max-width:600px) {
      .sp-info-grid { grid-template-columns:1fr !important; }
    }
  `;

  return (
    <div className="sp"><style>{sharedStyle+profileStyle}</style>
      <div className="sp-header"><div><div className="sp-title">My Profile</div><div className="sp-sub">View and update your information</div></div></div>
      <div style={{background:'linear-gradient(135deg,#8b5cf6,#6366f1)',borderRadius:16,padding:'1.4rem 1.6rem',color:'white',marginBottom:'1.2rem',display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
        <div style={{width:56,height:56,borderRadius:14,background:'rgba(255,255,255,.2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.1rem',fontWeight:700,flexShrink:0}}>{initials(profile?.name)}</div>
        <div>
          <div style={{fontSize:'1.1rem',fontWeight:700}}>{profile?.name}</div>
          <div style={{fontSize:'.76rem',opacity:.8,marginTop:2}}>{profile?.email}</div>
          <div style={{fontSize:'.72rem',opacity:.7,marginTop:4}}>{profile?.class} — Section {profile?.section} · Roll {profile?.rollNo} · {profile?.admissionNo}</div>
        </div>
      </div>
      <div style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'1rem 1.2rem',marginBottom:'1rem'}}>
        <div style={{fontSize:'.86rem',fontWeight:600,color:'#0f1123',marginBottom:12}}>Student Information</div>
        <div className="sp-info-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
          {[{label:'Admission No',value:profile?.admissionNo},{label:'Date of Birth',value:profile?.dateOfBirth?fmtDate(profile.dateOfBirth):'—'},{label:'Parent Name',value:profile?.parentName||'—'},{label:'Parent Contact',value:profile?.parentContact||'—'},{label:'Parent Email',value:profile?.parentEmail||'—'},{label:'Parent/Guardian CNIC',value:profile?.parentCnic||'—'},{label:'Address',value:profile?.address||'—'}].map(f=>(
            <div key={f.label}><div style={{fontSize:'.67rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:3}}>{f.label}</div><div style={{fontSize:'.82rem',color:'#1a1d2e',fontWeight:500}}>{f.value}</div></div>
          ))}
        </div>
      </div>
      <div className="panel">
        <div className="panel-head"><div className="panel-title">Change Password</div></div>
        <div style={{padding:'16px 18px',maxWidth:400}}>
          {[{label:'Current Password',key:'currentPassword'},{label:'New Password',key:'newPassword'},{label:'Confirm New Password',key:'confirm'}].map(f=>(
            <div key={f.key} className="form-field"><label className="form-label">{f.label}</label><PasswordInput className="form-input" value={pwForm[f.key]} onChange={e=>setPwForm({...pwForm,[f.key]:e.target.value})} /></div>
          ))}
          <button className="btn btn-purple" onClick={handleChangePw} disabled={pwSaving}>{pwSaving?'Changing…':'Change Password'}</button>
        </div>
      </div>
    </div>
  );
};

// ─── TIMETABLE ────────────────────────────────────────────────────────────────
const STT_DAYS   = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const STT_DOW    = { Monday:1, Tuesday:2, Wednesday:3, Thursday:4, Friday:5, Saturday:6, Sunday:0 };
const STT_COLORS = ['#3b6ef8','#10c98e','#8b5cf6','#f59e0b','#ef4444','#06b6d4','#ec4899','#14b8a6'];
const sttToMins  = (t) => { if (!t) return 0; const [h,m] = t.split(':').map(Number); return h*60+m; };
const sttColor   = (s, arr) => { if (!s) return '#9094a8'; const i = arr.indexOf(s); return STT_COLORS[i >= 0 ? i % STT_COLORS.length : 0]; };

const sttCss = `
  .stt { font-family:'Sora',sans-serif; color:#1a1d2e; }
  .stt * { box-sizing:border-box; }
  .stt-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; font-size:.82rem; }
  .stt-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.2rem; flex-wrap:wrap; gap:10px; }
  .stt-title  { font-size:1.2rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .stt-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .stt-vtoggle { display:flex; gap:3px; background:#f0f2f7; border-radius:10px; padding:3px; }
  .stt-vbtn { display:inline-flex; align-items:center; gap:5px; padding:6px 12px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; background:transparent; color:#9094a8; transition:all .15s; }
  .stt-vbtn.on { background:#fff; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }
  .stt-dtabs { display:flex; gap:4px; margin-bottom:1rem; overflow-x:auto; padding-bottom:4px; scrollbar-width:none; }
  .stt-dtabs::-webkit-scrollbar { display:none; }
  .stt-dtab { padding:5px 14px; border-radius:8px; border:1.5px solid #e0e4f0; background:#fff; font-family:'Sora',sans-serif; font-size:.74rem; font-weight:600; cursor:pointer; color:#9094a8; white-space:nowrap; flex-shrink:0; transition:all .15s; }
  .stt-dtab:hover { border-color:#3b6ef8; color:#3b6ef8; }
  .stt-dtab.on { background:#eef2ff; border-color:#3b6ef8; color:#3b6ef8; }
  .stt-dtab.off-tab { border-color:#fecaca; color:#ef4444; background:#fef2f2; }
  .stt-dtab.off-tab.on { background:#ef4444; color:white; border-color:#ef4444; }
  .stt-dtab.hol-tab { border-color:#a7f3d0; color:#10c98e; background:#ecfdf5; }
  .stt-panel { background:#fff; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .stt-panel-hdr { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; }
  .stt-panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .stt-lt { width:100%; border-collapse:collapse; }
  .stt-lt thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .stt-lt th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .stt-lt tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .stt-lt tbody tr:last-child { border-bottom:none; }
  .stt-lt tbody tr:hover { background:#fafbff; }
  .stt-lt td { padding:11px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .stt-pbadge { display:inline-flex; align-items:center; justify-content:center; width:28px; height:28px; border-radius:8px; background:#eef2ff; color:#3b6ef8; font-size:.76rem; font-weight:700; }
  .stt-pbadge.act { background:#fff7ed; color:#f97316; }
  .stt-dbadge { display:inline-flex; font-size:.72rem; font-weight:600; padding:3px 9px; border-radius:99px; }
  .stt-tcell  { font-size:.74rem; color:#6b7194; white-space:nowrap; }
  .stt-act-badge { display:inline-flex; align-items:center; gap:4px; font-size:.72rem; font-weight:700; padding:3px 9px; border-radius:99px; background:#fff7ed; color:#f97316; border:1px solid #fed7aa; }
  .stt-gwrap { overflow-x:auto; width:100%; }
  .stt-gt { border-collapse:collapse; min-width:600px; table-layout:fixed; }
  .stt-gt th { padding:10px 12px; font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; background:#fafbff; border:1px solid #edf0f9; text-align:center !important; white-space:nowrap; }
  .stt-gt th.dh { background:#f0f2f7; text-align:left; padding-left:14px; width:100px; min-width:90px; color:#6b7194; }
  .stt-gt th.lh { background:#fffbeb; color:#92400e; min-width:80px; }
  .stt-gt td { border:1px solid #edf0f9; padding:6px 8px; vertical-align:top; width:130px; min-width:110px; height:72px; text-align:center !important; }
  .stt-gt td.dc  { background:#f8f9fc; font-weight:700; font-size:.82rem; color:#0f1123; vertical-align:middle; padding:10px 14px; border-right:2px solid #e8ebf4; }
  .stt-gt td.odc { background:#fef9f9; color:#ef4444; font-weight:700; font-size:.82rem; vertical-align:middle; padding:10px 14px; border-right:2px solid #e8ebf4; }
  .stt-gt td.sdc { background:#f8f9fc; color:#9094a8; font-weight:700; font-size:.82rem; vertical-align:middle; padding:10px 14px; border-right:2px solid #e8ebf4; }
  .stt-gt td.hdc { background:#f0fdf4; color:#10c98e; font-weight:700; font-size:.82rem; vertical-align:middle; padding:10px 14px; border-right:2px solid #e8ebf4; }
  .stt-gt td.lc  { background:#fffbeb; text-align:center; vertical-align:middle; }
  .stt-gt td.ec  { text-align:center; color:#e0e4f0; font-size:.8rem; vertical-align:middle; }
  .stt-gt td.ofc { background:#fef9f9; vertical-align:middle; text-align:center; }
  .stt-gt td.snc { background:#f8f9fc; vertical-align:middle; text-align:center; }
  .stt-ge { border-radius:8px; padding:6px 8px; margin-bottom:3px; }
  .stt-ge-s { font-size:.76rem; font-weight:700; line-height:1.2; }
  .stt-ge-t { font-size:.66rem; margin-top:2px; }
  .stt-ge-tm { font-size:.62rem; margin-top:2px; }
  .stt-mob { display:none; }
  .stt-day-card { background:#fff; border-radius:12px; border:1px solid #e8ebf4; margin-bottom:10px; overflow:hidden; }
  .stt-day-card.ofc { border-color:#fecaca; }
  .stt-day-card.snc { border-color:#e0e4f0; }
  .stt-day-card.hlc { border-color:#a7f3d0; }
  .stt-day-hdr { display:flex; align-items:center; justify-content:space-between; padding:10px 14px; background:#f8f9fc; border-bottom:1px solid #edf0f9; }
  .stt-day-card.ofc .stt-day-hdr { background:#fef9f9; }
  .stt-day-card.snc .stt-day-hdr { background:#f8f9fc; }
  .stt-day-card.hlc .stt-day-hdr { background:#f0fdf4; }
  .stt-day-name  { font-size:.86rem; font-weight:700; color:#0f1123; }
  .stt-day-badge { font-size:.7rem; font-weight:600; padding:2px 8px; border-radius:99px; }
  .stt-pp { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #f3f5fb; }
  .stt-pp:last-child { border-bottom:none; }
  .stt-pn { display:inline-flex; align-items:center; justify-content:center; width:26px; height:26px; border-radius:7px; background:#eef2ff; color:#3b6ef8; font-size:.72rem; font-weight:700; flex-shrink:0; }
  .stt-pn.act { background:#fff7ed; color:#f97316; }
  .stt-pi { flex:1; }
  .stt-ps { font-size:.82rem; font-weight:700; }
  .stt-pm { font-size:.7rem; color:#6b7194; margin-top:2px; }
  .stt-lunch-pill { display:flex; align-items:center; gap:8px; padding:8px 14px; background:#fffbeb; border-bottom:1px solid #fde68a; font-size:.78rem; font-weight:600; color:#92400e; }
  .stt-hol-pill { padding:8px 14px; font-size:.78rem; background:#f0fdf4; border-bottom:1px solid #a7f3d0; }
  .stt-off-pill { padding:12px 14px; font-size:.78rem; color:#ef4444; font-weight:600; background:#fef9f9; }
  .stt-sun-pill { padding:12px 14px; font-size:.78rem; color:#9094a8; font-style:italic; background:#f8f9fc; }
  .stt-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  @media(max-width:768px) { .stt-gwrap { display:none; } .stt-lt-wrap { display:none !important; } .stt-mob { display:block; } }
`;

export const StudentTimetable = () => {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [viewMode,  setViewMode]  = useState('list');
  const [activeDay, setActiveDay] = useState('All');

  useEffect(() => {
    api.get('/student/timetable')
      .then(r => { setData(r.data); setViewMode(r.data?.timetableView || 'list'); })
      .catch(() => toast.error('Failed to load timetable'))
      .finally(() => setLoading(false));
  }, []);

  const tName = (t) => {
    if (!t) return '—';
    if (typeof t === 'object') return t.user?.name || t.name || '—';
    const s = String(t);
    if (/^\d+$/.test(s)) return '—'; // raw ID with no name — don't show number
    return s;
  };

  const entries    = data?.timetable     || [];
  const lunchStart = data?.lunchStart    || '';
  const lunchEnd   = data?.lunchEnd      || '';
  const offDays    = data?.weeklyOffDays || [0];
  const satPolicy  = data?.saturdayPolicy || 'all_off';
  const holidays   = data?.holidays      || [];

  const isOffDay = (dayName) => {
    if (dayName === 'Sunday') return true;
    if (dayName === 'Saturday') return satPolicy === 'all_off';
    const dow = STT_DOW[dayName];
    return (offDays || []).filter(d => d !== 6).includes(dow);
  };

  const holidaysByDay = {};
  holidays.forEach(h => {
    const dow = new Date(h.date).getDay();
    const day = STT_DAYS.find(d => STT_DOW[d] === dow);
    if (day) { if (!holidaysByDay[day]) holidaysByDay[day] = []; holidaysByDay[day].push(h); }
  });

  const getDayType = (day) => {
    if (day === 'Sunday') return 'sunday';
    if (isOffDay(day))    return 'off';
    if (holidaysByDay[day]?.length) return 'holiday';
    return 'normal';
  };

  const uniqSubjs  = [...new Set(entries.filter(e => !e.isActivity).map(e => e.subject).filter(Boolean))];
  const listDays   = activeDay === 'All' ? STT_DAYS : [activeDay];
  const allPeriods = [...new Set(entries.map(e => e.period))].sort((a,b) => a-b);
  const gridMap    = {};
  entries.forEach(e => {
    if (!gridMap[e.period]) gridMap[e.period] = {};
    if (!gridMap[e.period][e.day]) gridMap[e.period][e.day] = [];
    gridMap[e.period][e.day].push(e);
  });

  const lunchAfterIdx = () => {
    if (!lunchStart || !allPeriods.length) return -1;
    for (let i = 0; i < allPeriods.length - 1; i++) {
      const cur = entries.find(e => e.period === allPeriods[i]   && e.endTime);
      const nxt = entries.find(e => e.period === allPeriods[i+1] && e.startTime);
      if (cur && nxt && sttToMins(cur.endTime) <= sttToMins(lunchStart) && sttToMins(lunchStart) <= sttToMins(nxt.startTime)) return i;
    }
    return -1;
  };
  const lunchAfter = lunchAfterIdx();
  const cols = [];
  allPeriods.forEach((p,i) => { cols.push({t:'p',p}); if (i===lunchAfter) cols.push({t:'l'}); });

  const periodTime = (p) => {
    const e = entries.find(x => x.period === p && x.startTime);
    if (!e) return null;
    return e.endTime ? `${e.startTime}–${e.endTime}` : e.startTime;
  };

  if (loading) return <div className="stt"><style>{sttCss}</style><div className="stt-loading"><div className="spinner"/>Loading timetable…</div></div>;
  if (!data)   return <div className="stt"><style>{sttCss}</style><div className="stt-empty">No timetable found.</div></div>;

  return (
    <div className="stt">
      <style>{sttCss}</style>
      <div className="stt-header">
        <div>
          <div className="stt-title">My Timetable</div>
          <div className="stt-sub">{data.className} — Section {data.section}</div>
        </div>
        <div className="stt-vtoggle">
          <button className={`stt-vbtn${viewMode==='list'?' on':''}`} onClick={()=>setViewMode('list')}>≡ List</button>
          <button className={`stt-vbtn${viewMode==='grid'?' on':''}`} onClick={()=>setViewMode('grid')}>⊞ Grid</button>
        </div>
      </div>

      {viewMode==='list' && <>
        <div className="stt-dtabs">
          <button className={`stt-dtab${activeDay==='All'?' on':''}`} onClick={()=>setActiveDay('All')}>All</button>
          {STT_DAYS.map(d => {
            const type = getDayType(d);
            const cls  = type==='off'||type==='sunday'?' off-tab':type==='holiday'?' hol-tab':'';
            return <button key={d} className={`stt-dtab${cls}${activeDay===d?' on':''}`} onClick={()=>setActiveDay(d)}>{d.slice(0,3)}{type==='off'||type==='sunday'?' 📅':type==='holiday'?' 🎉':''}</button>;
          })}
        </div>

        <div className="stt-panel">
          <div className="stt-panel-hdr"><div className="stt-panel-title">{data.className} — Section {data.section}{activeDay!=='All'?` — ${activeDay}`:''}</div></div>
          <div className="stt-lt-wrap" style={{overflowX:'auto'}}>
            <table className="stt-lt">
              <thead><tr>
                <th style={{minWidth:70}}>Period</th>
                {activeDay==='All' && <th style={{minWidth:100}}>Day</th>}
                <th style={{minWidth:150}}>Time</th>
                <th style={{minWidth:150}}>Subject / Activity</th>
                <th style={{minWidth:130}}>Teacher</th>
              </tr></thead>
              <tbody>
                {listDays.map(day => {
                  const type  = getDayType(day);
                  const dayE  = entries.filter(e => e.day===day).sort((a,b)=>(a.period||0)-(b.period||0));
                  const dayH  = holidaysByDay[day]||[];
                  const showDay = activeDay==='All';

                  const holRows = dayH.map((h,hi) => (
                    <tr key={`hol-${day}-${hi}`} style={{background:'#f0fdf4'}}>
                      <td colSpan={showDay?5:4} style={{textAlign:'center',padding:'8px 16px'}}>
                        <span style={{fontWeight:700,color:'#065f46',fontSize:'.82rem'}}>🎉 {h.name}</span>
                      </td>
                    </tr>
                  ));

                  if (type==='sunday' && !dayE.length && !dayH.length) return (
                    <tr key={`sun-${day}`} style={{background:'#f8f9fc'}}>
                      <td><span style={{color:'#c0c5dc',fontSize:'.72rem'}}>—</span></td>
                      {showDay&&<td><span className="stt-dbadge" style={{background:'#f0f2f7',color:'#9094a8'}}>{day}</span></td>}
                      <td></td>
                      <td><span style={{fontSize:'.74rem',fontWeight:600,color:'#9094a8'}}>📅 Sunday — Weekly Off</span></td>
                      <td></td>
                    </tr>
                  );

                  if (type==='off' && !dayE.length && !dayH.length) return (
                    <tr key={`off-${day}`} style={{background:'#fef9f9'}}>
                      <td><span style={{color:'#fecaca',fontSize:'.72rem'}}>—</span></td>
                      {showDay&&<td><span className="stt-dbadge" style={{background:'#fef2f2',color:'#ef4444'}}>{day}</span></td>}
                      <td></td>
                      <td><span style={{fontSize:'.74rem',fontWeight:600,color:'#ef4444'}}>📅 Weekly Off</span></td>
                      <td></td>
                    </tr>
                  );

                  const periodRows = [];
                  let lunchDone = !lunchStart;
                  dayE.forEach((e,i) => {
                    if (!lunchDone && e.startTime && sttToMins(e.startTime) >= sttToMins(lunchEnd||lunchStart)) {
                      periodRows.push(<tr key={`lunch-${day}`} style={{background:'#fffbeb'}}>
                        <td colSpan={showDay?5:4} style={{padding:'8px 16px',fontSize:'.76rem',fontWeight:600,color:'#92400e',textAlign:'center'}}>
                          🍽 Lunch Break — {lunchStart}{lunchEnd?` – ${lunchEnd}`:''}
                        </td>
                      </tr>);
                      lunchDone = true;
                    }
                    if (e.isActivity) {
                      periodRows.push(<tr key={`act-${day}-${i}`}>
                        <td><span className="stt-pbadge act">🏃</span></td>
                        {showDay&&<td><span className="stt-dbadge" style={{background:'#eef2ff',color:'#3b6ef8'}}>{day}</span></td>}
                        <td><span className="stt-tcell">{e.startTime&&e.endTime?`${e.startTime} – ${e.endTime}`:e.startTime||e.endTime||'—'}</span></td>
                        <td><span className="stt-act-badge">🏃 {e.activityType||'Activity'}</span></td>
                        <td style={{fontSize:'.78rem',color:'#4a5075'}}>{tName(e.teacher)}</td>
                      </tr>);
                    } else {
                      const color = sttColor(e.subject, uniqSubjs);
                      periodRows.push(<tr key={`per-${day}-${i}`}>
                        <td><span className="stt-pbadge">{e.period}</span></td>
                        {showDay&&<td><span className="stt-dbadge" style={{background:'#eef2ff',color:'#3b6ef8'}}>{day}</span></td>}
                        <td><span className="stt-tcell">{e.startTime&&e.endTime?`${e.startTime} – ${e.endTime}`:e.startTime||e.endTime||'—'}</span></td>
                        <td><span style={{display:'inline-flex',fontSize:'.76rem',fontWeight:600,padding:'4px 10px',borderRadius:99,background:`${color}18`,color}}>{e.subject}</span></td>
                        <td style={{fontSize:'.78rem',color:'#4a5075'}}>{tName(e.teacher)}</td>
                      </tr>);
                    }
                  });

                  if (!dayE.length && dayH.length) return holRows;
                  return [...holRows, ...periodRows];
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards (list view) */}
          <div className="stt-mob">
            {listDays.map(day => {
              const type  = getDayType(day);
              const dayE  = entries.filter(e => e.day===day).sort((a,b)=>(a.period||0)-(b.period||0));
              const dayH  = holidaysByDay[day]||[];
              const cardCls = type==='sunday'?'stt-day-card snc':type==='off'?'stt-day-card ofc':type==='holiday'?'stt-day-card hlc':'stt-day-card';
              const badgeStyle = type==='sunday'?{background:'#f0f2f7',color:'#9094a8'}:type==='off'?{background:'#fef2f2',color:'#ef4444'}:type==='holiday'?{background:'#ecfdf5',color:'#10c98e'}:{background:'#eef2ff',color:'#3b6ef8'};
              const items=[]; let lDone=!lunchStart;
              dayE.forEach(e => {
                if(!lDone&&e.startTime&&sttToMins(e.startTime)>=sttToMins(lunchEnd||lunchStart)){items.push({type:'lunch'});lDone=true;}
                items.push({type:'period',e});
              });
              return (
                <div key={day} className={cardCls}>
                  <div className="stt-day-hdr"><span className="stt-day-name">{day}</span><span className="stt-day-badge" style={badgeStyle}>{type==='off'?'📅 Weekly Off':type==='sunday'?'📅 Off':type==='holiday'?'🎉 Holiday':`${dayE.length} period${dayE.length!==1?'s':''}`}</span></div>
                  {dayH.map((h,hi)=><div key={hi} className="stt-hol-pill"><span style={{fontWeight:700,color:'#065f46'}}>🎉 {h.name}</span></div>)}
                  {(type==='off'||type==='sunday')&&!dayE.length&&!dayH.length&&<div className={type==='sunday'?'stt-sun-pill':'stt-off-pill'}>{type==='sunday'?'📅 Sunday — Weekly Off':'📅 Weekly Off'}</div>}
                  {type==='normal'&&!dayE.length&&!dayH.length&&<div className="stt-empty" style={{padding:'14px'}}>No periods scheduled.</div>}
                  {items.map((item,idx)=>{
                    if(item.type==='lunch') return <div key="lunch" className="stt-lunch-pill">🍽 Lunch {lunchStart}{lunchEnd?` – ${lunchEnd}`:''}</div>;
                    const e=item.e; const color=sttColor(e.subject,uniqSubjs);
                    if(e.isActivity) return (
                      <div key={idx} className="stt-pp">
                        <span className="stt-pn act">🏃</span>
                        <div className="stt-pi">
                          <div className="stt-ps" style={{color:'#f97316'}}>🏃 {e.activityType||'Activity'}</div>
                          <div className="stt-pm">{tName(e.teacher)&&tName(e.teacher)!=='—'?tName(e.teacher)+' · ':''}{e.startTime&&<span>{e.startTime}{e.endTime?` – ${e.endTime}`:''}</span>}</div>
                        </div>
                      </div>
                    );
                    return (
                      <div key={idx} className="stt-pp">
                        <span className="stt-pn">{e.period}</span>
                        <div className="stt-pi">
                          <div className="stt-ps" style={{color}}>{e.subject}</div>
                          <div className="stt-pm">{tName(e.teacher)}{e.startTime&&<span style={{marginLeft:6,fontSize:'.68rem'}}>{e.startTime}{e.endTime?` – ${e.endTime}`:''}</span>}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </>}

      {viewMode==='grid' && <>
        <div className="stt-gwrap">
          <table className="stt-gt">
            <thead><tr>
              <th className="dh">Day</th>
              {cols.map((col,ci) => {
                if (col.t==='l') return <th key={`lh-${ci}`} className="lh">🍽 Lunch{lunchStart&&<div style={{fontSize:'.6rem',fontWeight:400,marginTop:2}}>{lunchStart}{lunchEnd?` – ${lunchEnd}`:''}</div>}</th>;
                return <th key={`ph-${col.p}`}><div>P{col.p}</div>{periodTime(col.p)&&<div style={{fontSize:'.6rem',fontWeight:400,color:'#b0b5cc',marginTop:2}}>{periodTime(col.p)}</div>}</th>;
              })}
            </tr></thead>
            <tbody>
              {STT_DAYS.map(day => {
                const type  = getDayType(day);
                const off   = type==='off'||type==='sunday';
                const dcCls = type==='sunday'?'sdc':type==='off'?'odc':type==='holiday'?'hdc':'dc';
                return (
                  <tr key={day}>
                    <td className={`dc ${dcCls}`}>
                      <div>{day}</div>
                      {type==='off'    &&<div style={{fontSize:'.62rem',color:'#ef4444',fontWeight:600,marginTop:2}}>📅 Off</div>}
                      {type==='sunday' &&<div style={{fontSize:'.62rem',color:'#9094a8',marginTop:2}}>📅 Off</div>}
                      {type==='holiday'&&<div style={{fontSize:'.62rem',color:'#10c98e',fontWeight:600,marginTop:2}}>🎉 Holiday</div>}
                    </td>
                    {off ? (
                      <td colSpan={cols.length||1} className={type==='sunday'?'snc':'ofc'}>
                        <span style={{fontSize:'.76rem',fontWeight:600,color:type==='sunday'?'#9094a8':'#ef4444'}}>{type==='sunday'?'📅 Sunday — Weekly Off':'📅 Weekly Off'}</span>
                      </td>
                    ) : cols.map((col,ci) => {
                      if (col.t==='l') return <td key={`lc-${ci}-${day}`} className="lc"><span style={{fontSize:'.8rem'}}>🍽</span></td>;
                      const cellE = (gridMap[col.p]?.[day]) || [];
                      if (!cellE.length) return <td key={`ec-${col.p}-${day}`} className="ec">—</td>;
                      return (
                        <td key={`tc-${col.p}-${day}`}>
                          {cellE.map((e,ei) => {
                            if (e.isActivity) return (
                              <div key={ei} className="stt-ge" style={{background:'#fff7ed',borderLeft:'3px solid #f97316',marginBottom:ei<cellE.length-1?3:0}}>
                                <div className="stt-ge-s" style={{color:'#f97316'}}>🏃 {e.activityType||'Activity'}</div>
                                {e.teacher&&<div className="stt-ge-t" style={{color:'#f97316cc'}}>{tName(e.teacher)}</div>}
                                {e.startTime&&<div className="stt-ge-tm" style={{color:'#f97316aa'}}>{e.startTime}{e.endTime?` – ${e.endTime}`:''}</div>}
                              </div>
                            );
                            const c2 = sttColor(e.subject, uniqSubjs);
                            return (
                              <div key={ei} className="stt-ge" style={{background:`${c2}18`,borderLeft:`3px solid ${c2}`,marginBottom:ei<cellE.length-1?3:0}}>
                                <div className="stt-ge-s" style={{color:c2}}>{e.subject}</div>
                                <div className="stt-ge-t" style={{color:`${c2}cc`}}>{tName(e.teacher)}</div>
                                {e.startTime&&<div className="stt-ge-tm" style={{color:`${c2}99`}}>{e.startTime}{e.endTime?` – ${e.endTime}`:''}</div>}
                              </div>
                            );
                          })}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="stt-mob">
          {STT_DAYS.map(day => {
            const type = getDayType(day);
            const dayH = holidaysByDay[day]||[];
            const dayE = entries.filter(e=>e.day===day).sort((a,b)=>(a.period||0)-(b.period||0));
            const cardCls = type==='sunday'?'stt-day-card snc':type==='off'?'stt-day-card ofc':type==='holiday'?'stt-day-card hlc':'stt-day-card';
            const badgeStyle = type==='sunday'?{background:'#f0f2f7',color:'#9094a8'}:type==='off'?{background:'#fef2f2',color:'#ef4444'}:type==='holiday'?{background:'#ecfdf5',color:'#10c98e'}:{background:'#eef2ff',color:'#3b6ef8'};
            const items=[]; let lDone=false;
            dayE.forEach(e => {
              if(!lDone&&lunchStart&&e.startTime){const[lh,lm]=lunchStart.split(':').map(Number);const[ph,pm]=e.startTime.split(':').map(Number);if(ph*60+pm>=lh*60+lm){items.push({type:'lunch'});lDone=true;}}
              items.push({type:'period',e});
            });
            if(lunchStart&&!lDone&&dayE.length) items.push({type:'lunch'});
            return (
              <div key={day} className={cardCls}>
                <div className="stt-day-hdr"><span className="stt-day-name">{day}</span><span className="stt-day-badge" style={badgeStyle}>{type==='off'?'📅 Weekly Off':type==='sunday'?'📅 Off':type==='holiday'?'🎉 Holiday':`${dayE.length} period${dayE.length!==1?'s':''}`}</span></div>
                {dayH.map((h,hi)=><div key={hi} className="stt-hol-pill"><span style={{fontWeight:700,color:'#065f46'}}>🎉 {h.name}</span></div>)}
                {(type==='off'||type==='sunday')&&!dayE.length&&<div className={type==='sunday'?'stt-sun-pill':'stt-off-pill'}>{type==='sunday'?'No classes — Sunday':'No classes scheduled'}</div>}
                {items.map((item,idx)=>{
                  if(item.type==='lunch') return <div key="lunch" className="stt-lunch-pill">🍽 Lunch {lunchStart}{lunchEnd?` – ${lunchEnd}`:''}</div>;
                  const e=item.e; const color=sttColor(e.subject,uniqSubjs);
                  if(e.isActivity) return (
                    <div key={idx} className="stt-pp">
                      <span className="stt-pn act">🏃</span>
                      <div className="stt-pi">
                        <div className="stt-ps" style={{color:'#f97316'}}>🏃 {e.activityType||'Activity'}</div>
                        <div className="stt-pm">{tName(e.teacher)&&tName(e.teacher)!=='—'?tName(e.teacher)+' · ':''}{e.startTime&&<span>{e.startTime}{e.endTime?` – ${e.endTime}`:''}</span>}</div>
                      </div>
                    </div>
                  );
                  return (
                    <div key={idx} className="stt-pp">
                      <span className="stt-pn">{e.period}</span>
                      <div className="stt-pi">
                        <div className="stt-ps" style={{color}}>{e.subject}</div>
                        <div className="stt-pm">{tName(e.teacher)}{e.startTime&&<span style={{marginLeft:6,fontSize:'.68rem'}}>{e.startTime}{e.endTime?` – ${e.endTime}`:''}</span>}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </>}
    </div>
  );
};

export default StudentAttendance;

// ─── GRADE REPORT (Term-Based) ───────────────────────────────────────────────
export const StudentGradeReport = () => (
  <div className="sp"><TermGradeReportCard endpoint="/student/grade-report" title="Grade Report" resultsPath="/student/results" accent="#8b5cf6"/></div>
);
